/**
 * Voice Socket Service - OpenAI Realtime / legacy voice connections
 * 
 * Primary path:
 * - Web/iOS/Android: OpenAI Realtime WebRTC (gpt-realtime-2)
 * 
 * Legacy fallback code remains for:
 * - Android: Socket.IO at /voice namespace
 * - iOS: Raw WebSocket at /voice-ws
 */

import { Platform } from 'react-native';
import io from 'socket.io-client';
import { SOCKET_HOST } from '@/constants/config';
import { devLog, devWarn } from '@/lib/devLog';
import { voiceApi } from '@/lib/api';
import {
  configureNativeRealtimeAudio,
  getRealtimeWebRTCAdapter,
  restoreNativeRealtimeAudio,
} from '@/lib/voiceRealtimeWebRTCAdapter';

// Lazy imports to avoid circular dependency
const useVoiceModeStore = { getState: () => require('@/store').useVoiceModeStore.getState() };
const useAuthStore = { getState: () => require('@/store').useAuthStore.getState() };
const useChatStore = { getState: () => require('@/store').useChatStore.getState() };
const VOICE_MODE_STATES = require('@/store').VOICE_MODE_STATES;

let nativeAudioManager;
const getNativeAudioManager = () => {
  if (Platform.OS === 'web') {
    throw new Error('Native audio is unavailable on web');
  }
  nativeAudioManager ||= require('@/lib/voiceSocketNative').default;
  return nativeAudioManager;
};

// iOS speaker-route startup briefly feeds playback into the mic. Realtime's
// server still truncates the WebRTC output buffer on that VAD event even with
// interrupt_response: false, which sounds like the first word or two then a
// cut. Mute server VAD only for this lockout after audio starts, then turn it
// back on so the user can barge in for the rest of the reply.
export const IOS_RESPONSE_ECHO_GUARD_MS = 1200;

// Safety net: VAD is muted as soon as the user stops talking, before we know
// whether a reply is coming. If the reply never produces audio, nothing would
// re-arm the precise lockout above and the mic would stay deaf for the rest of
// the call. Always restore by this deadline.
export const IOS_VAD_LOCKOUT_MAX_MS = 8000;

// iOS speakerphone VAD: require clearer speech than the OpenAI 0.5 default
// so rustle, echo tails, and room noise do not barge in. Matched to the
// backend iOS profile in realtime_voice_config.js.
export const IOS_VAD_THRESHOLD = 0.75;
export const IOS_VAD_PREFIX_PADDING_MS = 300;
export const IOS_VAD_SILENCE_DURATION_MS = 900;
export const IOS_NOISE_REDUCTION = { type: 'far_field' };

// After lockout, wait this long of continued speech before cutting the
// assistant. Brief blips (a bump, an echo spike) die before this fires.
export const IOS_BARGE_IN_CONFIRM_MS = 400;

/**
 * Voice Socket Manager
 * Manages the Socket.IO connection to /voice namespace
 */
export class VoiceSocketManager {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.sessionId = null;
    this.conversationId = null;
    this.voiceId = null;
    
    // Session resumption state
    this.isResuming = false;
    this.resumableSessionId = null;
    this.disconnectReason = null;
    
    // Audio playback queue for TTS
    this.audioContext = null;
    this.audioQueue = [];
    this.isPlaying = false;
    this.currentSource = null;
    
    // VAD (Voice Activity Detection) for interruption
    this.vadAnalyser = null;
    this.vadSourceNode = null; // Store source node so we can disconnect it
    // Detect iOS for platform-specific adjustments
    this.isIOS = Platform.OS === 'ios' || (Platform.OS === 'web' && typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent));
    // Default thresholds (will be adjusted by setVadConfig)
    // Needs to be high enough to not trigger on AI's TTS audio from speakers
    this.vadThreshold = 35;
    this.vadCheckInterval = null;
    this.silenceThresholdMs = this.isIOS ? 700 : 500; // Longer silence for iOS (slower processing)
    this.vadDebounceMs = 100;
    this.lastSoundTime = null;
    this.lastEndUtteranceTime = null;
    this.hasAudioChunks = false;
    
    // Debug status for troubleshooting (especially on mobile where console isn't visible)
    this.debugStatus = {
      audioFormat: null,
      audioContextState: null,
      mediaRecorderState: null,
      chunksReceived: 0,
      speechDetected: false,
      lastVolume: 0,
      errors: [],
    };
    
    // Speech detection tracking to prevent Whisper hallucinations
    this.speechDetected = false; // Track if actual speech was detected in current utterance
    this.speechStartTime = 0; // When speech started
    this.minSpeechDurationMs = this.isIOS ? 600 : 800; // Shorter for iOS
    this.minAudioChunks = this.isIOS ? 5 : 8; // Fewer chunks required for iOS (~500ms)
    this.minAudioEnergyRms = 0.008; // Minimum RMS energy threshold to filter silence/noise
    
    // MediaRecorder for audio capture
    this.mediaRecorder = null;
    this.audioStream = null;
    this.audioChunks = []; // Buffer chunks until utterance ends
    this.recorderMimeType = null;
    
    // Transcript buffering for media generation
    // When the user speaks, we buffer the final transcript text and only commit it
    // to the chat store after we see the ai_response. If ai_response.isMediaGeneration
    // is true, the server already saved the user message so we skip adding it locally.
    this.pendingTranscriptText = null;
    this.pendingTranscriptTimeout = null;
    
    // Callbacks
    this.onStateChange = null;
    this.onTranscript = null;
    this.onAiResponse = null;
    this.onError = null;
    this.onSessionResumable = null;
    this.onResumeSuccess = null;
    this.onResumeFailed = null;
    
    // Vision callbacks
    this.onVisionStarted = null;
    this.onVisionContext = null;
    this.onVisionStopped = null;
    this.onVisionError = null;

    // Raw WebSocket mode (iOS only)
    this._useRawWebSocket = false;
    this._rawWsPingInterval = null;

    // OpenAI Realtime WebRTC mode (web)
    this._useRealtimeWebRTC = false;
    this.peerConnection = null;
    this.dataChannel = null;
    this.remoteAudioElement = null;
    this.remoteAudioStream = null;
    this._assistantTranscriptBuffer = '';
    this._assistantTextBuffer = '';
    // Citations from the most recent voice web_search tool call — attached
    // to the next assistant transcript save / chat message.
    this._pendingSearchCitations = null;
    this._pendingSearchQuery = null;
    this._webSearchInFlight = false;

    // Realtime turn tracking — prevents the chat from getting duplicate
    // messages when a single OpenAI response contains multiple output items
    // (e.g. reasoning traces alongside the actual audio answer) or when the
    // same event is emitted more than once across the data channel.
    this._committedResponseIds = new Set();
    this._lastUserTranscriptItemId = null;
    this._currentResponseId = null;
    // True between `response.created` and `response.done`. Used to:
    //   1. Detect when the model spuriously kicks off a *second* response for
    //      the same user turn (most often caused by speaker-on-mic echo
    //      tripping semantic_vad while the AI is still talking) and cancel it.
    //   2. Drive the post-response `input_audio_buffer.clear` so any echo that
    //      did sneak in doesn't trigger a follow-up response after `done`.
    this._responseInFlight = false;
    // True while server-side VAD says the user is mid-speech. Used to skip
    // the post-response `input_audio_buffer.clear`: when the user barges in,
    // the cancelled response's `done` event still arrives moments later and
    // an unconditional clear would wipe the start of the user's interruption.
    this._userSpeechActive = false;
    this._responseAudioStartedAt = 0;
    this._suppressingLikelyEcho = false;
    // iOS playback echo trips server VAD even with interrupt_response: false.
    // Snapshot the detector on session.created, mute it for the startup
    // lockout, then restore it so barge-in works for the rest of the reply.
    this._iosTurnDetection = null;
    this._iosVadSuspended = false;
    this._iosVadRestoreTimer = null;
    this._iosBargeInPending = false;
    this._iosBargeInTimer = null;
    this._nativeRemoteTrackRouteConfigured = false;
    this._lastNativeAudioRouteConfigAt = 0;
    // Track the timestamp of the most recent `response.cancel` we sent. We
    // use this to suppress the benign `Cancellation failed: no active
    // response found` error OpenAI emits when our cancel races with the
    // response naturally completing server-side. Without this guard the
    // error gets surfaced as a fatal "Connection Error" modal even though
    // the user just heard the assistant finish speaking. (See iOS bug
    // where the modal pops up immediately after a normal turn.)
    this._lastCancelSentAt = 0;
    // Grace period during which an `error` event matching the benign cancel
    // pattern is swallowed instead of surfaced.
    this._cancelErrorSuppressMs = 4000;
    // Last visual-context description injected into the realtime
    // conversation, used to skip re-sending identical context.
    this._lastInjectedVisualContext = null;
    // Pending `_saveRealtimeTranscript` promises. We track them so we can
    // await all of them on disconnect/end-call — otherwise the request that
    // sets the voice session to `status: 'completed'` can race ahead of the
    // last few transcript-save POSTs, the backend rejects them with 404
    // (route requires `status: 'active'`), the local voice messages then age
    // past the 30s freshness window in `addChats`, and the entire chat
    // transcript appears to "disappear" when the call ends.
    this._pendingTranscriptSaves = new Set();

    // Mic prewarm: acquire getUserMedia in parallel with the REST session
    // creation so the user isn't waiting on mic acquisition *after* the
    // network round trips. Web only — native audio routing must be
    // configured before the mic is opened.
    this._micPrewarmPromise = null;
    this._prewarmedStream = null;

    // Debounce transient `disconnected` WebRTC states. The spec allows a
    // peer connection to drop to `disconnected` briefly (network blip,
    // cellular handoff, iOS background → foreground) and then climb back
    // to `connected`. Surfacing an error the instant we see `disconnected`
    // produces false "Realtime voice connection was interrupted" modals
    // mid-call.
    this._disconnectTimeout = null;
    this._disconnectGraceMs = 5000;
  }

  /**
   * Initialize Web Audio context for TTS playback and VAD
   * CRITICAL: On iOS Safari, AudioContext starts suspended and must be resumed
   * NOTE: On native platforms (iOS/Android), audio is handled by nativeAudioManager instead
   */
  async _initAudioContext() {
    // Skip web audio context on native platforms - nativeAudioManager handles audio
    if (Platform.OS !== 'web') {
      devLog('[VoiceSocket] Skipping web audio context on native platform');
      return;
    }

    if (this.audioContext && this.audioContext.state !== 'closed') {
      // Resume if suspended (common on iOS after tab switch or background)
      if (this.audioContext.state === 'suspended') {
        try {
          await this.audioContext.resume();
          devLog('[VoiceSocket] Audio context resumed from suspended state');
        } catch (err) {
          devWarn('[VoiceSocket] Failed to resume audio context:', err);
        }
      }
      return;
    }
    
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContext();
      devLog('[VoiceSocket] Audio context initialized, state:', this.audioContext.state);
      
      // iOS Safari: AudioContext starts suspended, must resume after user gesture
      // Since this is called during voice mode start (user clicked), we can resume immediately
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
        devLog('[VoiceSocket] Audio context resumed after init, state:', this.audioContext.state);
      }
    } catch (err) {
      console.error('[VoiceSocket] Failed to init audio context:', err);
    }
  }

  /**
   * Connect to voice mode transport.
   * Primary path is OpenAI Realtime WebRTC on web/iOS/Android.
   * Legacy Socket.IO/raw WebSocket branches remain below as fallbacks for
   * non-standard platforms or future rollback.
   * @param {string} sessionId - Voice session ID from API
   * @param {string} conversationId - Conversation ID
   * @param {string} voiceId - Selected voice ID
   * @param {string} [voiceSessionToken] - Legacy voice session token
   */
  async connect(sessionId, conversationId, voiceId, voiceSessionToken = null) {
    if (this._isConnected()) {
      devLog('[VoiceSocket] Already connected');
      return;
    }

    this.sessionId = sessionId;
    this.conversationId = conversationId;
    this.voiceId = voiceId;

    if (['web', 'ios', 'android', 'macos'].includes(Platform.OS)) {
      return this._connectRealtimeWebRTC(sessionId, conversationId, voiceId);
    }

    // Initialize audio context (web only)
    await this._initAudioContext();

    // iOS uses raw WebSocket at /voice-ws; web and Android use Socket.IO
    const useRawWebSocket = Platform.OS === 'ios';

    if (useRawWebSocket) {
      return this._connectRawWebSocket(sessionId);
    }

    // Socket.IO path (web, Android)
    const authStore = useAuthStore.getState();
    const token = authStore.token;

    if (!token) {
      console.error('[VoiceSocket] No auth token available');
      throw new Error('Authentication required');
    }

    console.log('[VoiceSocket] Connecting to /voice namespace (Socket.IO)...');

    const isBrowser = Platform.OS === 'web' && typeof window !== 'undefined' && typeof window.location !== 'undefined';
    const isSameOrigin = isBrowser && window.location.origin && SOCKET_HOST === window.location.origin;
    const socketUrl = isSameOrigin ? '/voice' : `${SOCKET_HOST}/voice`;

    console.log('[VoiceSocket] Connecting to:', socketUrl, 'Platform:', Platform.OS);
    this.socket = io(socketUrl, {
      auth: {
        token: token,
        sessionId: sessionId,
        platform: Platform.OS,
      },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      forceNew: true,
    });

    this._setupEventHandlers();

    return new Promise((resolve, reject) => {
      const timeoutMs = Platform.OS === 'web' ? 10000 : 15000;
      let settled = false;
      const settleAndCleanup = (callback) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        this.socket?.off('connect', onConnect);
        this.socket?.off('connect_error', onConnectError);
        callback();
      };

      const onConnect = () => {
        settleAndCleanup(() => {
          console.log('[VoiceSocket] Connected');
          resolve();
        });
      };

      const onConnectError = (error) => {
        settleAndCleanup(() => {
          console.error('[VoiceSocket] Connection error:', error.message);
          reject(error);
        });
      };

      const timeout = setTimeout(() => {
        settleAndCleanup(() => {
          try {
            this.socket?.disconnect();
          } catch (_) {}
          reject(new Error('Connection timeout'));
        });
      }, timeoutMs);

      this.socket.on('connect', onConnect);
      this.socket.on('connect_error', onConnectError);
    });
  }

  /**
   * Connect via raw WebSocket (iOS only)
   * Uses JWT access token for auth (same token as REST API).
   * Protocol: JSON messages { event: "eventName", data: {...} }
   * URL: wss://host/voice-ws?token=<jwt>&platform=ios[&sessionId=<id>]
   */
  async _connectRawWebSocket(sessionId) {
    const authStore = useAuthStore.getState();
    const token = authStore.token;
    if (!token) {
      throw new Error('Authentication required for iOS voice mode');
    }

    const baseUrl = SOCKET_HOST || '';
    const wsScheme = baseUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = baseUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '');
    const wsPath = '/voice-ws';
    const params = new URLSearchParams({
      token: token,
      platform: 'ios',
    });
    // Include sessionId for resume if available
    if (sessionId) {
      params.set('sessionId', sessionId);
    }
    const wsUrl = `${wsScheme}://${wsHost}${wsPath}?${params.toString()}`;

    console.log('[VoiceSocket] Connecting to raw WebSocket (iOS):', wsPath);

    return new Promise((resolve, reject) => {
      const timeoutMs = 15000;
      let settled = false;
      const settleAndCleanup = (callback) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        callback();
      };

      try {
        this.socket = new WebSocket(wsUrl);
        this._useRawWebSocket = true;
      } catch (err) {
        reject(err);
        return;
      }

      this.socket.onopen = () => {
        settleAndCleanup(() => {
          this.isConnected = true;
          console.log('[VoiceSocket] Raw WebSocket connected');
          this._setupRawWebSocketEventHandlers();
          resolve();
        });
      };

      this.socket.onerror = (event) => {
        settleAndCleanup(() => {
          console.error('[VoiceSocket] Raw WebSocket error:', event);
          reject(new Error(event.message || 'WebSocket connection failed'));
        });
      };

      this.socket.onclose = (event) => {
        if (!settled) {
          settleAndCleanup(() => {
            reject(new Error(event.reason || 'WebSocket closed'));
          });
        }
      };

      const timeout = setTimeout(() => {
        settleAndCleanup(() => {
          try {
            this.socket?.close();
          } catch (_) {}
          reject(new Error('Connection timeout'));
        });
      }, timeoutMs);
    });
  }

  /**
   * Start acquiring the microphone now so it's (usually) ready by the time
   * the backend session exists. Safe to call multiple times; the stream is
   * consumed by the next _connectRealtimeWebRTC and cleaned up on disconnect.
   * Web only.
   */
  prewarmMicrophone() {
    if (Platform.OS !== 'web') return null;
    if (this._micPrewarmPromise) return this._micPrewarmPromise;

    const { mediaDevices } = getRealtimeWebRTCAdapter();
    if (!mediaDevices?.getUserMedia) return null;

    devLog('[VoiceSocket] Prewarming microphone...');
    const prewarmPromise = mediaDevices
      .getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })
      .then((stream) => {
        // If the prewarm was cancelled while getUserMedia was pending (call
        // ended / cleanup ran before the mic resolved), release the tracks
        // immediately — otherwise the browser keeps the recording indicator
        // on with no way to ever stop the orphaned stream.
        if (this._micPrewarmPromise !== prewarmPromise) {
          try {
            stream.getTracks().forEach((track) => track.stop());
          } catch (_) { /* ignore */ }
          return stream;
        }
        this._prewarmedStream = stream;
        return stream;
      })
      .catch((err) => {
        // Clear so connect() retries getUserMedia itself and surfaces the
        // real permission error through the normal path.
        if (this._micPrewarmPromise === prewarmPromise) {
          this._micPrewarmPromise = null;
        }
        throw err;
      });
    this._micPrewarmPromise = prewarmPromise;
    return prewarmPromise;
  }

  async _connectRealtimeWebRTC(sessionId, conversationId, voiceId) {
    if (!sessionId) {
      throw new Error('Voice session ID is required for realtime voice mode');
    }
    const {
      RTCPeerConnection,
      RTCSessionDescription,
      mediaDevices,
      isNative,
    } = getRealtimeWebRTCAdapter();

    if (!RTCPeerConnection || !mediaDevices?.getUserMedia) {
      throw new Error(
        isNative
          ? 'Realtime voice requires native WebRTC support. Rebuild the app with react-native-webrtc.'
          : 'Realtime voice requires WebRTC support in this browser.'
      );
    }

    this._useRealtimeWebRTC = true;
    this.sessionId = sessionId;
    this.conversationId = conversationId;
    this.voiceId = voiceId || 'marin';
    this._assistantTranscriptBuffer = '';
    this._assistantTextBuffer = '';
    this._nativeRemoteTrackRouteConfigured = false;

    const { setState } = useVoiceModeStore.getState().actions;

    devLog('[VoiceSocket] Connecting via OpenAI Realtime WebRTC...', { platform: Platform.OS });

    // Android: do NOT touch the audio route yet. Forcing MODE_IN_COMMUNICATION /
    // speaker while getUserMedia and WebRTC's own audio device are initializing
    // can deadlock Samsung's audio service (One UI, Android 12+) and hard-freeze
    // the app. The route is configured once, after the data channel opens.
    // iOS keeps the early call so AVAudioSession is set up before the mic starts.
    if (Platform.OS !== 'android') {
      await this._configureNativeRealtimeAudioRoute('connect-start', true);
    }

    const pc = new RTCPeerConnection();
    this.peerConnection = pc;

    pc.onconnectionstatechange = () => {
      devLog('[VoiceSocket] Realtime connection state:', pc.connectionState);
      this.isConnected = ['connected', 'connecting'].includes(pc.connectionState);

      if (this._disconnectTimeout && pc.connectionState !== 'disconnected') {
        clearTimeout(this._disconnectTimeout);
        this._disconnectTimeout = null;
      }

      if (pc.connectionState === 'failed') {
        this.onError?.('Realtime voice connection was interrupted.');
        return;
      }

      // `disconnected` is transient per the WebRTC spec — give the ICE agent
      // a few seconds to recover (network blip, cellular handoff, iOS
      // background) before treating it as a hard error.
      if (pc.connectionState === 'disconnected') {
        if (this._disconnectTimeout) return;
        this._disconnectTimeout = setTimeout(() => {
          this._disconnectTimeout = null;
          if (!this.peerConnection || this.peerConnection !== pc) return;
          if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
            this.onError?.('Realtime voice connection was interrupted.');
          }
        }, this._disconnectGraceMs);
      }
    };

    const handleRemoteTrack = (event) => {
      const [stream] = event.streams || [];
      if (!stream) return;
      if (isNative) {
        // react-native-webrtc renders remote audio through the native WebRTC
        // audio device once a remote audio track is attached. Keep a reference
        // so the stream is not GC'd while the peer connection is active.
        this.remoteAudioStream = stream;
        // WebRTC may rewrite AVAudioSession after the track is attached.
        // Re-apply the iOS speaker route exactly once here. Reconfiguring on
        // every streamed audio delta can interrupt the live output.
        // Android is configured once on data-channel open instead (see above about
        // Samsung audio-service deadlocks during connection setup).
        if (Platform.OS !== 'android' && !this._nativeRemoteTrackRouteConfigured) {
          this._nativeRemoteTrackRouteConfigured = true;
          this._configureNativeRealtimeAudioRoute('remote-track', true);
        }
        return;
      }
      if (!this.remoteAudioElement) {
        this.remoteAudioElement = document.createElement('audio');
        this.remoteAudioElement.autoplay = true;
        this.remoteAudioElement.playsInline = true;
      }
      this.remoteAudioElement.srcObject = stream;
      const playPromise = this.remoteAudioElement.play?.();
      if (playPromise?.catch) {
        playPromise.catch((err) => devWarn('[VoiceSocket] Remote audio autoplay failed:', err?.message || err));
      }
    };
    // Register exactly one handler. Registering both `ontrack` and an
    // addEventListener listener fires the handler twice on web, which
    // re-sets srcObject mid-load and aborts the first play() call.
    if (typeof pc.addEventListener === 'function') {
      pc.addEventListener('track', handleRemoteTrack);
    } else {
      pc.ontrack = handleRemoteTrack;
    }

    // Use the prewarmed mic stream if one is ready/pending (started in
    // parallel with the REST session creation); fall back to a fresh
    // getUserMedia. Permission errors propagate either way.
    let micStream = null;
    if (this._micPrewarmPromise) {
      try {
        micStream = await this._micPrewarmPromise;
      } catch (_) {
        micStream = null;
      }
      this._micPrewarmPromise = null;
      this._prewarmedStream = null;
    }
    const hasLiveTrack = micStream?.getAudioTracks?.().some((t) => t.readyState === 'live');
    if (!micStream || !hasLiveTrack) {
      const gumPromise = mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      if (Platform.OS === 'web') {
        micStream = await gumPromise;
      } else {
        // Native: getUserMedia can hang indefinitely inside the native bridge
        // on some devices (seen on Samsung when the audio service is busy or
        // wedged). Bound it so the user gets an error overlay instead of a
        // frozen app. If the native call eventually resolves after the
        // timeout, release the tracks immediately so the mic isn't orphaned.
        micStream = await new Promise((resolve, reject) => {
          let timedOut = false;
          const timer = setTimeout(() => {
            timedOut = true;
            reject(new Error('AUDIO_INPUT_TIMEOUT: Voice input did not start in time. Please close other apps that may be using audio and try again.'));
          }, 10000);
          gumPromise.then(
            (stream) => {
              clearTimeout(timer);
              if (timedOut) {
                try {
                  stream.getTracks().forEach((track) => track.stop());
                } catch (_) { /* ignore */ }
                return;
              }
              resolve(stream);
            },
            (err) => {
              clearTimeout(timer);
              if (!timedOut) reject(err);
            }
          );
        });
      }
    }
    this.audioStream = micStream;

    this.audioStream.getAudioTracks().forEach((track) => pc.addTrack(track, this.audioStream));

    await this._initAudioContext();
    this._setupRealtimeInputMeter();

    const dc = pc.createDataChannel('oai-events');
    this.dataChannel = dc;
    this.socket = dc;

    const handleDataChannelOpen = () => {
      devLog('[VoiceSocket] Realtime data channel open');
      this.isConnected = true;
      // Android: the connection is fully established now, so this is the safe
      // moment to force the communication audio route (speaker + voice-call
      // stream). Fire-and-forget — routing must never block the session.
      if (Platform.OS === 'android') {
        this._configureNativeRealtimeAudioRoute('datachannel-open', true);
      }
      setState(VOICE_MODE_STATES.LISTENING);
      this.onStateChange?.('listening');
    };

    dc.onopen = handleDataChannelOpen;

    dc.onmessage = (event) => {
      this._handleRealtimeEvent(event.data);
    };

    dc.onerror = (event) => {
      console.error('[VoiceSocket] Realtime data channel error:', event);
      this.onError?.('Realtime voice data channel error.');
    };

    dc.onclose = () => {
      devLog('[VoiceSocket] Realtime data channel closed');
      this.isConnected = false;
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const answerSdp = await voiceApi.createRealtimeAnswer(sessionId, offer.sdp);
    const answerDescription = RTCSessionDescription
      ? new RTCSessionDescription({ type: 'answer', sdp: answerSdp })
      : { type: 'answer', sdp: answerSdp };
    await pc.setRemoteDescription(answerDescription);

    return new Promise((resolve, reject) => {
      if (dc.readyState === 'open') {
        resolve();
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('Realtime connection timeout'));
      }, 15000);

      const onOpen = () => {
        clearTimeout(timeout);
        dc.removeEventListener?.('open', onOpen);
        resolve();
      };

      dc.addEventListener?.('open', onOpen);
      dc.onopen = () => {
        onOpen();
        handleDataChannelOpen();
      };
    });
  }

  _setupRealtimeInputMeter() {
    if (!this._isAudioContextValid() || !this.audioStream) return;

    try {
      this.vadSourceNode = this.audioContext.createMediaStreamSource(this.audioStream);
      this.vadAnalyser = this.audioContext.createAnalyser();
      this.vadAnalyser.fftSize = 256;
      this.vadSourceNode.connect(this.vadAnalyser);
    } catch (err) {
      devWarn('[VoiceSocket] Failed to setup realtime input meter:', err?.message || err);
    }
  }

  async _configureNativeRealtimeAudioRoute(reason = 'unknown', force = false) {
    if (Platform.OS === 'web') return;

    const now = Date.now();
    if (!force && now - (this._lastNativeAudioRouteConfigAt || 0) < 2000) {
      return;
    }
    this._lastNativeAudioRouteConfigAt = now;

    try {
      // Guard with a timeout: on some devices (notably Samsung/One UI) the
      // underlying AudioManager calls can block inside the system audio
      // service. A wedged native call must not hang voice mode startup —
      // the session works without the route config, just possibly quieter.
      let timeoutId = null;
      const timeoutPromise = new Promise((_, rejectTimeout) => {
        timeoutId = setTimeout(
          () => rejectTimeout(new Error('Audio route configuration timed out')),
          4000
        );
      });
      try {
        await Promise.race([configureNativeRealtimeAudio(), timeoutPromise]);
      } finally {
        clearTimeout(timeoutId);
      }
      devLog('[VoiceSocket] Native realtime audio route configured:', reason);
    } catch (err) {
      devWarn('[VoiceSocket] Failed to configure native realtime audio route:', reason, err?.message || err);
    }
  }

  _sendRealtimeEvent(event) {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open') return false;
    this.dataChannel.send(JSON.stringify(event));
    return true;
  }

  // Remember a response id as committed/cancelled, trimming the oldest
  // entries so the set doesn't grow unbounded over a long session.
  _rememberCommittedResponse(responseId) {
    if (!responseId) return;
    this._committedResponseIds.add(responseId);
    if (this._committedResponseIds.size > 200) {
      const oldest = this._committedResponseIds.values().next().value;
      this._committedResponseIds.delete(oldest);
    }
  }

  _defaultIosTurnDetection() {
    return {
      type: 'server_vad',
      threshold: IOS_VAD_THRESHOLD,
      prefix_padding_ms: IOS_VAD_PREFIX_PADDING_MS,
      silence_duration_ms: IOS_VAD_SILENCE_DURATION_MS,
      create_response: true,
      interrupt_response: false,
    };
  }

  _isPreferredIosTurnDetection(turnDetection) {
    return Boolean(
      turnDetection &&
      turnDetection.type === 'server_vad' &&
      turnDetection.threshold === IOS_VAD_THRESHOLD &&
      turnDetection.interrupt_response === false &&
      turnDetection.create_response === true
    );
  }

  _turnDetectionFromSession(session) {
    const turnDetection = session?.audio?.input?.turn_detection || session?.turn_detection;
    if (!turnDetection || typeof turnDetection !== 'object') return null;
    return { ...turnDetection };
  }

  _iosAudioInput(turnDetection) {
    // Do not re-send noise_reduction on session.update. The backend SDP
    // already applied it when OpenAI accepted the field, and retries without
    // it when the provider rejects it. Re-attaching far_field here can fail
    // every VAD suspend/restore after that fallback.
    return {
      turn_detection: turnDetection,
    };
  }

  _sendIosTurnDetection(turnDetection) {
    if (Platform.OS !== 'ios') return false;
    return this._sendRealtimeEvent({
      type: 'session.update',
      session: {
        type: 'realtime',
        audio: {
          input: this._iosAudioInput(turnDetection),
        },
      },
    });
  }

  // Push the less-sensitive iOS detector so faint speaker/ambient noise
  // cannot barge in. interrupt_response stays off: the client confirms
  // a real interruption before cancelling.
  _enforceClientOwnedInterruption(session) {
    if (Platform.OS !== 'ios') return false;

    const preferred = this._defaultIosTurnDetection();
    this._iosTurnDetection = { ...preferred };
    const current = this._turnDetectionFromSession(session);
    if (this._isPreferredIosTurnDetection(current)) return false;

    devLog('[VoiceSocket] Applying iOS high-threshold VAD and taking over barge-in');
    return this._sendIosTurnDetection(preferred);
  }

  _clearIosVadRestoreTimer() {
    if (!this._iosVadRestoreTimer) return;
    clearTimeout(this._iosVadRestoreTimer);
    this._iosVadRestoreTimer = null;
  }

  _clearIosBargeInTimer() {
    if (!this._iosBargeInTimer) return;
    clearTimeout(this._iosBargeInTimer);
    this._iosBargeInTimer = null;
  }

  _rejectIosBargeIn() {
    const hadPending = this._iosBargeInPending;
    this._clearIosBargeInTimer();
    this._iosBargeInPending = false;
    if (!hadPending) return false;
    this._userSpeechActive = false;
    this._sendRealtimeEvent({ type: 'input_audio_buffer.clear' });
    return true;
  }

  _armIosBargeInConfirm() {
    if (Platform.OS !== 'ios' || this._iosBargeInPending) return;
    this._clearIosBargeInTimer();
    this._iosBargeInPending = true;
    this._iosBargeInTimer = setTimeout(() => {
      this._iosBargeInTimer = null;
      if (!this._iosBargeInPending || !this._responseInFlight) {
        this._iosBargeInPending = false;
        return;
      }
      this._iosBargeInPending = false;
      this._userSpeechActive = true;
      this._cancelRealtimeResponseForInterruption();
      const { setState } = useVoiceModeStore.getState().actions;
      setState(VOICE_MODE_STATES.SPEAKING);
      this.onStateChange?.('speaking');
    }, IOS_BARGE_IN_CONFIRM_MS);
  }

  // interrupt_response: false is not enough on iOS. The Realtime server still
  // emits output_audio_buffer.cleared when VAD hears speaker echo, which cuts
  // playback after a word or two. Mute turn detection only for the startup
  // lockout; restoring it is what keeps barge-in working.
  _suspendIosTurnDetection() {
    if (Platform.OS !== 'ios' || this._iosVadSuspended) return false;
    this._clearIosVadRestoreTimer();
    this._rejectIosBargeIn();
    this._iosVadSuspended = true;
    this._userSpeechActive = false;
    if (!this._iosTurnDetection) {
      this._iosTurnDetection = this._defaultIosTurnDetection();
    }
    devLog('[VoiceSocket] Suspending iOS turn detection for playback lockout');
    const sent = this._sendIosTurnDetection(null);
    this._armIosVadRestore(IOS_VAD_LOCKOUT_MAX_MS);
    return sent;
  }

  _armIosVadRestore(delayMs) {
    if (Platform.OS !== 'ios') return;
    this._clearIosVadRestoreTimer();
    this._iosVadRestoreTimer = setTimeout(() => {
      this._iosVadRestoreTimer = null;
      if (!this._iosVadSuspended) return;
      // Drop echo that accumulated while VAD was off so restoring it does
      // not immediately fire speech_started and cut the rest of the reply.
      this._sendRealtimeEvent({ type: 'input_audio_buffer.clear' });
      this._restoreIosTurnDetection();
    }, delayMs);
  }

  _restoreIosTurnDetection() {
    if (Platform.OS !== 'ios' || !this._iosVadSuspended) return false;
    this._clearIosVadRestoreTimer();
    this._iosVadSuspended = false;
    const turnDetection = {
      ...(this._iosTurnDetection || this._defaultIosTurnDetection()),
      create_response: true,
      interrupt_response: false,
    };
    if (!turnDetection.type) {
      Object.assign(turnDetection, this._defaultIosTurnDetection());
    }
    devLog('[VoiceSocket] Restoring iOS turn detection for barge-in');
    return this._sendIosTurnDetection(turnDetection);
  }

  _cancelRealtimeResponseForInterruption() {
    if (!this._useRealtimeWebRTC || !this._responseInFlight) return false;

    const responseId = this._currentResponseId;
    devLog('[VoiceSocket] Cancelling realtime response for user interruption', responseId);
    const sent = this._sendRealtimeEvent({
      type: 'response.cancel',
      ...(responseId ? { response_id: responseId } : {}),
    });

    if (sent) {
      this._lastCancelSentAt = Date.now();
      this._rememberCommittedResponse(responseId);
      this._responseInFlight = false;
      this._currentResponseId = null;
      this._assistantTranscriptBuffer = '';
      this._assistantTextBuffer = '';
      this._responseAudioStartedAt = 0;
      this._suppressingLikelyEcho = false;
      this._clearIosBargeInTimer();
      this._iosBargeInPending = false;
      this._restoreIosTurnDetection();
    }

    return sent;
  }

  _isBenignCancelError(event) {
    const code = event?.error?.code || event?.code || '';
    const message = String(
      event?.error?.message || event?.message || ''
    ).toLowerCase();
    const matchesCancelCode =
      code === 'response_cancel_not_active' ||
      code === 'response_cancel_no_active_response';
    const matchesCancelMessage =
      message.includes('cancellation failed') ||
      message.includes('no active response') ||
      (message.includes('cancel') && message.includes('active response'));
    if (matchesCancelCode || matchesCancelMessage) return true;

    // Even when OpenAI changes wording, a cancel error within the suppress
    // window after we sent a cancel is almost certainly the same race.
    const recentlyCancelled =
      this._lastCancelSentAt &&
      Date.now() - this._lastCancelSentAt < this._cancelErrorSuppressMs;
    if (recentlyCancelled && message.includes('cancel')) return true;

    return false;
  }

  _handleRealtimeEvent(raw) {
    let event;
    try {
      event = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (err) {
      devWarn('[VoiceSocket] Failed to parse realtime event:', err?.message || err);
      return;
    }

    const { setState, setUserTranscript, setPartialTranscript, setAiTranscript } = useVoiceModeStore.getState().actions;

    switch (event.type) {
      case 'session.created':
        devLog('[VoiceSocket] Realtime session event:', event.type);
        this._enforceClientOwnedInterruption(event.session);
        break;

      case 'session.updated':
        devLog('[VoiceSocket] Realtime session event:', event.type);
        break;

      case 'input_audio_buffer.speech_started':
        // During the iOS startup lockout, speech_started is echo — ignore it
        // and do not cancel. After VAD is restored, a short confirmation
        // window still has to pass so faint blips do not cut the reply.
        if (Platform.OS === 'ios' && this._responseInFlight && this._iosVadSuspended) {
          this._suppressingLikelyEcho = true;
          this._userSpeechActive = false;
          devLog('[VoiceSocket] Ignoring iOS speech during playback lockout');
          break;
        }
        this._suppressingLikelyEcho = false;
        this._userSpeechActive = true;
        if (Platform.OS === 'ios' && this._responseInFlight) {
          this._armIosBargeInConfirm();
          break;
        }
        this._cancelRealtimeResponseForInterruption();
        setState(VOICE_MODE_STATES.SPEAKING);
        this.onStateChange?.('speaking');
        break;

      case 'input_audio_buffer.speech_stopped':
      case 'input_audio_buffer.committed':
        if (this._suppressingLikelyEcho) {
          this._userSpeechActive = false;
          this._sendRealtimeEvent({ type: 'input_audio_buffer.clear' });
          setState(VOICE_MODE_STATES.SPEAKING);
          this.onStateChange?.('speaking');
          break;
        }
        if (Platform.OS === 'ios' && this._responseInFlight && !this._iosVadSuspended) {
          // A blip during playback that died before the confirm window, or
          // the committed event that follows it. Keep the assistant talking.
          if (!this._rejectIosBargeIn()) {
            this._userSpeechActive = false;
            this._sendRealtimeEvent({ type: 'input_audio_buffer.clear' });
          }
          setState(VOICE_MODE_STATES.SPEAKING);
          this.onStateChange?.('speaking');
          break;
        }
        this._userSpeechActive = false;
        // Disable VAD before the assistant audio starts. Waiting until
        // response.created is too late: the first audio packet and the echo
        // VAD event often arrive together, and the server clears the output
        // buffer before a session.update can land.
        this._suspendIosTurnDetection();
        setState(VOICE_MODE_STATES.PROCESSING);
        this.onStateChange?.('processing');
        break;

      case 'conversation.item.input_audio_transcription.delta': {
        const next = `${useVoiceModeStore.getState().partialTranscript || ''}${event.delta || ''}`;
        setPartialTranscript(next);
        this.onTranscript?.(next, false);
        break;
      }

      case 'conversation.item.input_audio_transcription.completed': {
        const transcript = event.transcript || '';
        const itemId = event.item_id || null;
        // Dedup: the same item_id can occasionally be re-broadcast.
        if (itemId && itemId === this._lastUserTranscriptItemId) {
          devLog('[VoiceSocket] Duplicate user transcript for item', itemId, '- skipping');
          break;
        }
        if (itemId) this._lastUserTranscriptItemId = itemId;
        if (transcript.trim()) {
          setUserTranscript(transcript);
          setPartialTranscript('');
          this._addMessageToChat('user', transcript);
          this._saveRealtimeTranscript('user', transcript);
          this.onTranscript?.(transcript, true);
        }
        break;
      }

      case 'response.created': {
        const newResponseId = event.response?.id || event.response_id || null;
        // If a response is already in flight when a new one is created, this
        // is almost always echo: the AI's own audio bled into the mic, the
        // turn detector tripped, and the server queued a second response for
        // the same user turn. Cancel it instead of letting it generate.
        // (We can't fully prevent it server-side because `interrupt_response:
        // false` only blocks restart-during-response; once the first response
        // ends, the queued one will still fire unless we cancel.)
        if (this._responseInFlight && newResponseId && newResponseId !== this._currentResponseId) {
          devLog('[VoiceSocket] Spurious overlapping response detected, cancelling', newResponseId);
          const cancelSent = this._sendRealtimeEvent({ type: 'response.cancel', response_id: newResponseId });
          if (cancelSent) this._lastCancelSentAt = Date.now();
          // Pre-mark as committed so the audio_transcript.done handler skips it
          // even if the cancel races with the model emitting a transcript.
          this._rememberCommittedResponse(newResponseId);
          break;
        }
        this._responseInFlight = true;
        setState(VOICE_MODE_STATES.PROCESSING);
        this._assistantTranscriptBuffer = '';
        this._assistantTextBuffer = '';
        this._currentResponseId = newResponseId;
        this._responseAudioStartedAt = 0;
        this._suppressingLikelyEcho = false;
        this._suspendIosTurnDetection();
        break;
      }

      case 'response.output_audio.delta':
        if (!this._responseAudioStartedAt) {
          this._responseAudioStartedAt = Date.now();
          // Audio is flowing, so start the real lockout from this moment and
          // drop the coarse safety-net deadline.
          this._armIosVadRestore(IOS_RESPONSE_ECHO_GUARD_MS);
        }
        setState(VOICE_MODE_STATES.SPEAKING);
        this.onStateChange?.('speaking');
        break;

      case 'response.output_audio_transcript.delta':
        this._assistantTranscriptBuffer += event.delta || '';
        setAiTranscript(this._assistantTranscriptBuffer);
        break;

      // NOTE: response.output_text.* events are intentionally ignored.
      // With output_modalities: ['audio'] the only user-facing output is the
      // spoken audio + its transcript. Any text output items the model emits
      // (reasoning summaries, tool-use chatter, alternative drafts) must NOT
      // be committed as separate chat messages — the user kept seeing things
      // like "Let me think about what's realistic here." appear as a real AI
      // turn. The audio transcript is the canonical assistant message.

      case 'response.output_audio_transcript.done': {
        const responseId = event.response_id || this._currentResponseId || null;
        // Dedup: only commit one assistant message per OpenAI response, even
        // if the response contains multiple audio output items or the done
        // event fires more than once.
        if (responseId && this._committedResponseIds.has(responseId)) {
          devLog('[VoiceSocket] Assistant message already committed for response', responseId, '- skipping duplicate');
          break;
        }
        const text = event.transcript || this._assistantTranscriptBuffer || '';
        if (text.trim()) {
          this._rememberCommittedResponse(responseId);
          setAiTranscript(text);
          const citations = this._pendingSearchCitations;
          this._addMessageToChat('assistant', text, citations);
          this._saveRealtimeTranscript('assistant', text, citations);
          this._pendingSearchCitations = null;
          this.onAiResponse?.(text, 'realtime');
        }
        break;
      }

      case 'response.done': {
        // Handle Realtime function calls (web_search) before clearing buffers.
        // Function-only responses have no audio transcript; we execute the
        // tool and ask the model for a spoken follow-up.
        const functionCalls = Array.isArray(event.response?.output)
          ? event.response.output.filter((item) => item?.type === 'function_call')
          : [];
        if (functionCalls.length > 0) {
          // Don't clear the mic buffer yet — keep the turn open while we search.
          this._responseInFlight = false;
          this._restoreIosTurnDetection();
          setState(VOICE_MODE_STATES.PROCESSING);
          this.onStateChange?.('processing');
          this._handleRealtimeFunctionCalls(functionCalls).catch((err) => {
            console.error('[VoiceSocket] Realtime function call handling failed:', err);
            useVoiceModeStore.getState().actions.setSearchResultsError?.(
              err?.message || 'Web search failed'
            );
            setState(VOICE_MODE_STATES.LISTENING);
            this.onStateChange?.('listening');
          });
          break;
        }

        // Flush any audio that arrived in the input buffer while the AI was
        // talking. This is the primary defense against double-responses
        // caused by speaker-on-mic echo: even with browser AEC and
        // `interrupt_response: false`, semantic_vad will sometimes commit
        // the AI's own voice as a "user turn" and queue a second response.
        // Clearing the buffer at the moment the AI finishes drops that
        // accumulated audio before VAD can act on it.
        //
        // EXCEPT while the user is actively speaking: when a barge-in cancels
        // the response, this `done` event still arrives a moment later, and
        // clearing then would clip the start of the user's interruption.
        if (!this._userSpeechActive) {
          this._sendRealtimeEvent({ type: 'input_audio_buffer.clear' });
        } else {
          devLog('[VoiceSocket] Skipping input_audio_buffer.clear - user speech in progress');
        }
        this._responseInFlight = false;
        this._responseAudioStartedAt = 0;
        this._suppressingLikelyEcho = false;
        this._restoreIosTurnDetection();
        setState(VOICE_MODE_STATES.LISTENING);
        this.onStateChange?.('listening');
        break;
      }

      case 'response.output_audio.done':
        // Audio finished; response.done may still arrive for function calls.
        // Only flip to listening when no tool call is in flight and we are
        // not waiting on a function-only response.follow-up.
        if (!this._webSearchInFlight && !this._responseInFlight) {
          if (!this._userSpeechActive) {
            this._sendRealtimeEvent({ type: 'input_audio_buffer.clear' });
          }
          setState(VOICE_MODE_STATES.LISTENING);
          this.onStateChange?.('listening');
        }
        break;

      case 'response.cancelled':
        this._responseInFlight = false;
        this._responseAudioStartedAt = 0;
        this._suppressingLikelyEcho = false;
        if (!this._userSpeechActive) {
          this._sendRealtimeEvent({ type: 'input_audio_buffer.clear' });
        }
        this._restoreIosTurnDetection();
        setState(VOICE_MODE_STATES.LISTENING);
        this.onStateChange?.('listening');
        break;

      case 'error':
      case 'invalid_request_error': {
        // OpenAI's Realtime API emits errors with the real payload under
        // `event.error` (type/code/message). Older defensive code also
        // checked `event.message` for non-spec cases.
        if (this._isBenignCancelError(event)) {
          devLog(
            '[VoiceSocket] Suppressing benign cancel error (response already finished):',
            event?.error?.message || event?.message || event
          );
          // Best-effort sync of local state. If the server tells us there
          // is no active response, our local flag is stale.
          this._responseInFlight = false;
          break;
        }
        console.error('[VoiceSocket] Realtime error:', event);
        this.onError?.(
          event?.error?.message || event?.message || 'Realtime voice error.'
        );
        break;
      }

      case 'rate_limits.updated':
        devLog('[VoiceSocket] Realtime rate limits updated');
        break;

      default:
        break;
    }
  }

  async _saveRealtimeTranscript(role, content, citations = null) {
    if (!this.sessionId || !content?.trim()) return;
    const savePromise = (async () => {
      try {
        await voiceApi.saveTranscript(
          this.sessionId,
          this.conversationId,
          role,
          content,
          citations
        );
      } catch (err) {
        devWarn('[VoiceSocket] Failed to save realtime transcript:', err?.message || err);
      }
    })();
    this._pendingTranscriptSaves.add(savePromise);
    savePromise.finally(() => this._pendingTranscriptSaves.delete(savePromise));
    return savePromise;
  }

  /**
   * Execute Realtime function_call items (currently web_search) and feed
   * results back so the model can speak a brief summary.
   */
  async _handleRealtimeFunctionCalls(functionCalls) {
    for (const call of functionCalls) {
      const name = call?.name;
      const callId = call?.call_id;
      if (!callId || !name) continue;

      if (name === 'web_search') {
        await this._executeRealtimeWebSearch(callId, call.arguments);
      } else {
        devWarn('[VoiceSocket] Unknown Realtime function:', name);
        this._sendRealtimeEvent({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: callId,
            output: JSON.stringify({ error: `Unknown function: ${name}` }),
          },
        });
      }
    }

    // Ask the model to continue with a spoken answer using tool results.
    this._sendRealtimeEvent({ type: 'response.create' });
  }

  async _executeRealtimeWebSearch(callId, argumentsJson) {
    const actions = useVoiceModeStore.getState().actions;
    let query = '';
    try {
      const parsed = typeof argumentsJson === 'string'
        ? JSON.parse(argumentsJson || '{}')
        : (argumentsJson || {});
      query = typeof parsed.query === 'string' ? parsed.query.trim() : '';
    } catch (err) {
      devWarn('[VoiceSocket] Failed to parse web_search arguments:', err?.message || err);
    }

    if (!query) {
      query = useVoiceModeStore.getState().userTranscript || this._pendingSearchQuery || '';
    }

    this._webSearchInFlight = true;
    this._pendingSearchQuery = query;
    actions.setSearchResultsSearching?.(query);

    let outputPayload = {
      success: false,
      query,
      results: [],
      message: 'No results',
    };

    try {
      if (!this.sessionId) {
        throw new Error('No active voice session for search');
      }
      const data = await voiceApi.search(this.sessionId, query);
      const results = Array.isArray(data.results) ? data.results : [];
      const citations = Array.isArray(data.citations) ? data.citations : [];
      this._pendingSearchCitations = citations.length > 0 ? citations : null;
      actions.setSearchResults?.({
        query: data.query || query,
        results,
        citations,
      });
      outputPayload = {
        success: true,
        query: data.query || query,
        empty: results.length === 0,
        // Compact payload for the spoken model — enough to summarize, not dump.
        results: results.slice(0, 5).map((r, index) => ({
          rank: index + 1,
          title: r.title,
          hostname: r.hostname,
          snippet: (r.snippet || '').slice(0, 220),
          url: r.url,
        })),
        instruction:
          'Speak a brief 1-3 sentence summary. Tell the user the top web page results are shown on screen. Do not read every URL.',
      };
    } catch (err) {
      console.error('[VoiceSocket] web_search failed:', err);
      actions.setSearchResultsError?.(err?.message || 'Web search failed');
      outputPayload = {
        success: false,
        query,
        error: err?.message || 'Web search failed',
        instruction: 'Apologize briefly that web search failed and offer to try again.',
      };
    } finally {
      this._webSearchInFlight = false;
    }

    this._sendRealtimeEvent({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: JSON.stringify(outputPayload),
      },
    });
  }

  /**
   * Wait for all in-flight transcript saves to settle. Call this from the
   * end-call flow before disconnecting / ending the backend session so the
   * last user turn + assistant reply make it into Mongo before the session
   * status flips to `completed` (after which the backend rejects further
   * /transcript writes).
   *
   * Resolves either when every pending save has settled or when `timeoutMs`
   * elapses — we never want this to block call-end indefinitely if the
   * network is dead.
   */
  async flushPendingTranscriptSaves(timeoutMs = 3000) {
    const pending = Array.from(this._pendingTranscriptSaves);
    if (pending.length === 0) return;
    devLog('[VoiceSocket] Flushing', pending.length, 'pending transcript save(s)');
    let timeoutHandle;
    const timeout = new Promise((resolve) => {
      timeoutHandle = setTimeout(resolve, timeoutMs);
    });
    try {
      await Promise.race([Promise.allSettled(pending), timeout]);
    } finally {
      if (timeoutHandle) clearTimeout(timeoutHandle);
    }
  }

  /**
   * Check if connected (works for both Socket.IO and raw WebSocket)
   */
  _isConnected() {
    if (this._useRealtimeWebRTC && this.dataChannel) {
      return this.dataChannel.readyState === 'open';
    }
    if (this._useRawWebSocket && this.socket) {
      return this.socket.readyState === WebSocket.OPEN;
    }
    return this.socket?.connected;
  }

  /**
   * Set up Socket.IO event handlers
   */
  _setupEventHandlers() {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      this.isConnected = true;
      console.log('[VoiceSocket] Connected to /voice');
      
      // If we have a session ID and this is a reconnection, attempt to resume
      if (this.sessionId && this.disconnectReason) {
        devLog('[VoiceSocket] Reconnected after disconnect, checking for resumable session');
        // The server will emit 'session_resumable' if there's a session to resume
        // We can also proactively attempt resume if we have a session ID
        this._attemptResumeOnReconnect();
      }
      
      // Clear disconnect reason after handling
      this.disconnectReason = null;
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      this.disconnectReason = reason;
      console.log('[VoiceSocket] Disconnected:', reason);
      this._stopAudioPlayback();
      
      // Don't clear session ID on unexpected disconnects - we may be able to resume
      // Session ID is only cleared on intentional disconnect() calls
      if (reason === 'io client disconnect') {
        // Intentional disconnect - clear session
        this.sessionId = null;
        this.resumableSessionId = null;
      } else {
        // Unexpected disconnect - keep session ID for potential resumption
        this.resumableSessionId = this.sessionId;
        devLog('[VoiceSocket] Unexpected disconnect, session may be resumable:', this.sessionId);
      }
    });

    // Session resumption events
    this.socket.on('session_resumable', (data) => {
      devLog('[VoiceSocket] Resumable session found:', data);
      const { sessionId, conversationId, timeRemainingMs, message } = data;
      
      this.resumableSessionId = sessionId;
      
      // Automatically attempt to resume the session
      if (sessionId) {
        devLog('[VoiceSocket] Auto-resuming session, time remaining:', timeRemainingMs, 'ms');
        this.resumeSession(sessionId);
      }
      
      if (this.onSessionResumable) {
        this.onSessionResumable(data);
      }
    });

    this.socket.on('resume_success', (data) => {
      devLog('[VoiceSocket] Session resumed after', data.disconnectDuration, 'ms');
      const { setState } = useVoiceModeStore.getState().actions;
      
      this.isResuming = false;
      this.sessionId = data.sessionId;
      this.conversationId = data.conversationId;
      
      // Restore state from server
      switch (data.state) {
        case 'listening':
          setState(VOICE_MODE_STATES.LISTENING);
          break;
        case 'processing':
          setState(VOICE_MODE_STATES.PROCESSING);
          break;
        case 'speaking':
          setState(VOICE_MODE_STATES.SPEAKING);
          break;
        default:
          setState(VOICE_MODE_STATES.LISTENING);
      }
      
      if (this.onStateChange) {
        this.onStateChange(data.state || 'listening');
      }
      
      if (this.onResumeSuccess) {
        this.onResumeSuccess(data);
      }
    });

    this.socket.on('resume_failed', (data) => {
      devLog('[VoiceSocket] Resume failed:', data.reason, '-', data.message);
      
      this.isResuming = false;
      this.resumableSessionId = null;
      
      // Save session ID before clearing so callback can use it for cleanup
      const failedSessionId = this.sessionId;
      
      // Clear session since it can't be resumed
      this.sessionId = null;
      
      if (this.onResumeFailed) {
        // Include the session ID in the callback data for backend cleanup
        this.onResumeFailed({ ...data, sessionId: failedSessionId });
      }
    });

    // Session ready - server confirms session is active
    this.socket.on('ready', (data) => {
      console.log('[VoiceSocket] Session ready:', data);
      const { setState } = useVoiceModeStore.getState().actions;
      setState(VOICE_MODE_STATES.LISTENING);
      
      if (this.onStateChange) {
        this.onStateChange('listening');
      }
    });

    // State changes from server
    this.socket.on('state', (data) => {
      devLog('[VoiceSocket] State change:', data.state);
      const { setState } = useVoiceModeStore.getState().actions;
      
      // Track when state changes for race-condition handling in _handleNativeUtteranceEnd
      this._lastStateChangeTime = Date.now();
      
      switch (data.state) {
        case 'listening':
          setState(VOICE_MODE_STATES.LISTENING);
          break;
        case 'processing':
          setState(VOICE_MODE_STATES.PROCESSING);
          break;
        case 'speaking':
          setState(VOICE_MODE_STATES.SPEAKING);
          // CRITICAL: Reset speech detection state when AI starts speaking.
          // This prevents the microphone from picking up TTS audio (echo)
          // and treating it as user speech. Any accumulated audio chunks
          // from during the processing phase are discarded.
          this._resetSpeechDetectionState();
          break;
      }
      
      if (this.onStateChange) {
        this.onStateChange(data.state);
      }
    });

    // User speech transcript from Whisper
    this.socket.on('transcript', (data) => {
      const { text, duration, isFinal } = data;
      devLog('[VoiceSocket] Transcript:', text?.substring(0, 50), '...', isFinal ? '(final)' : '(partial)');
      
      const { setUserTranscript, setPartialTranscript } = useVoiceModeStore.getState().actions;
      
      if (isFinal) {
        setUserTranscript(text);
        setPartialTranscript('');
        
        // Buffer the transcript text instead of immediately adding to chat.
        // We wait for the subsequent ai_response to check isMediaGeneration.
        // If isMediaGeneration is true, the server already saved the user message
        // to the DB, so we skip adding it locally to prevent duplicates.
        this.pendingTranscriptText = text;
        devLog('[VoiceSocket] Buffered transcript, waiting for ai_response', {
          hasConversationId: !!this.conversationId,
          conversationId: this.conversationId,
          duration,
        });

        if (this.pendingTranscriptTimeout) {
          clearTimeout(this.pendingTranscriptTimeout);
          this.pendingTranscriptTimeout = null;
        }

        // Guard against silent transcript loss if ai_response never arrives.
        this.pendingTranscriptTimeout = setTimeout(() => {
          if (!this.pendingTranscriptText) return;
          console.warn('[VoiceSocket] ai_response timeout after final transcript; committing buffered user message', {
            hasConversationId: !!this.conversationId,
            conversationId: this.conversationId,
          });
          this._addMessageToChat('user', this.pendingTranscriptText);
          this.pendingTranscriptText = null;
          this.pendingTranscriptTimeout = null;
        }, 8000);
      } else {
        setPartialTranscript(text);
      }
      
      if (this.onTranscript) {
        this.onTranscript(text, isFinal);
      }
    });

    // Transcript rejected by backend (hallucination detected)
    this.socket.on('transcript_rejected', (data) => {
      devLog('[VoiceSocket] Transcript rejected:', data.reason, data.text ? `("${data.text.substring(0, 30)}...")` : '');

      // Clear any buffered transcript since it was rejected
      this.pendingTranscriptText = null;
      if (this.pendingTranscriptTimeout) {
        clearTimeout(this.pendingTranscriptTimeout);
        this.pendingTranscriptTimeout = null;
      }
      
      // Return to listening state
      const { setState, setPartialTranscript } = useVoiceModeStore.getState().actions;
      setState(VOICE_MODE_STATES.LISTENING);
      setPartialTranscript(''); // Clear any partial transcript
      
      if (this.onStateChange) {
        this.onStateChange('listening');
      }
    });

    // AI response text
    this.socket.on('ai_response', (data) => {
      const { text, intent, isMediaGeneration } = data;
      devLog('[VoiceSocket] AI response:', text?.substring(0, 50), '...',
        intent ? `(${intent})` : '',
        isMediaGeneration ? '(media generation)' : '');
      
      const { setAiTranscript } = useVoiceModeStore.getState().actions;
      setAiTranscript(text);
      
      // Handle the buffered user transcript.
      // If this is a media generation response, the server already saved the user
      // message to the conversation DB via saveUserTranscriptOnly(), so we do NOT
      // add it locally. The message will appear when the conversation is refreshed
      // or when the poll response includes the updated conversation.
      if (this.pendingTranscriptText) {
        if (this.pendingTranscriptTimeout) {
          clearTimeout(this.pendingTranscriptTimeout);
          this.pendingTranscriptTimeout = null;
        }
        if (isMediaGeneration) {
          devLog('[VoiceSocket] Media generation detected - skipping local user message (server saved it)');
        } else {
          // Not media generation - commit the buffered user message to chat store
          this._addMessageToChat('user', this.pendingTranscriptText);
        }
        this.pendingTranscriptText = null;
      }
      
      // Add assistant response to chat store (with any pending search citations)
      const citations = this._pendingSearchCitations;
      this._addMessageToChat('assistant', text, citations);
      this._pendingSearchCitations = null;
      
      if (this.onAiResponse) {
        this.onAiResponse(text, intent);
      }
    });

    // Structured web search results (legacy voice path) — SERP panel
    this.socket.on('search_results', (data) => {
      const results = Array.isArray(data?.results) ? data.results : [];
      const citations = Array.isArray(data?.citations) ? data.citations : [];
      this._pendingSearchCitations = citations.length > 0 ? citations : null;
      useVoiceModeStore.getState().actions.setSearchResults?.({
        query: data?.query || '',
        results,
        citations,
      });
    });

    // TTS audio chunk from Kokoro
    this.socket.on('audio', async (data) => {
      const { buffer, format, sampleRate, isLast } = data;
      devLog('[VoiceSocket] Audio chunk received, isLast:', isLast);

      try {
        await this._queueAudioChunk(buffer, sampleRate);
      } catch (err) {
        console.error('[VoiceSocket] Error processing audio:', err);
      }
    });

    // Interrupt acknowledged
    this.socket.on('interrupt_ack', (data) => {
      devLog('[VoiceSocket] Interrupt acknowledged:', data.message);
      this._stopAudioPlayback();
    });

    // Voice changed
    this.socket.on('voice_changed', (data) => {
      devLog('[VoiceSocket] Voice changed to:', data.voiceId);
      this.voiceId = data.voiceId;
      const { setSelectedVoice } = useVoiceModeStore.getState().actions;
      setSelectedVoice(data.voiceId);
    });

    // Error handling
    this.socket.on('error', (data) => {
      console.error('[VoiceSocket] Error:', data.message);
      if (this.onError) {
        this.onError(data.message);
      }
    });

    // Vision events
    this.socket.on('vision_started', (data) => {
      devLog('[VoiceSocket] Vision started:', data);
      const { startVision } = useVoiceModeStore.getState().actions;
      startVision(data.source || 'camera', data.config);
      if (this.onVisionStarted) {
        this.onVisionStarted(data);
      }
    });

    this.socket.on('vision_context', (data) => {
      devLog('[VoiceSocket] Vision context:', data.visualContext?.substring(0, 100), '...', 
        data.cached ? '(cached)' : '', 'frame:', data.frameNumber);
      const { setVisualContext } = useVoiceModeStore.getState().actions;
      if (data.visualContext) {
        setVisualContext(data.visualContext);
      }
      
      // Notify vision service that a frame was processed (for backpressure handling)
      try {
        const voiceVisionService = require('@/lib/voiceVision').default;
        voiceVisionService.onFrameProcessed();
      } catch (e) {
        // Ignore - vision service may not be loaded
      }
      
      if (this.onVisionContext) {
        this.onVisionContext(data);
      }
    });

    this.socket.on('vision_stopped', (data) => {
      devLog('[VoiceSocket] Vision stopped, frames analyzed:', data.framesAnalyzed);
      const { stopVision } = useVoiceModeStore.getState().actions;
      stopVision();
      if (this.onVisionStopped) {
        this.onVisionStopped(data);
      }
    });

    this.socket.on('vision_error', (data) => {
      console.error('[VoiceSocket] Vision error:', data.message);
      if (this.onVisionError) {
        this.onVisionError(data.message);
      }
    });
  }

  /**
   * Set up raw WebSocket event handlers (iOS)
   * Server sends JSON: { event: "eventName", data: {...} }
   */
  _setupRawWebSocketEventHandlers() {
    if (!this.socket || !this._useRawWebSocket) return;

    const handlers = {
      connected: (data) => {
        console.log('[VoiceSocket] Raw WebSocket connected event:', data?.message);
        this.disconnectReason = null;
      },
      ready: (data) => {
        console.log('[VoiceSocket] Session ready:', data);
        const { setState, setConversationId } = useVoiceModeStore.getState().actions;
        setState(VOICE_MODE_STATES.LISTENING);
        
        // Store sessionId and conversationId from the server's ready event.
        // On iOS, these are the authoritative values — the session was created
        // by the WebSocket 'start' event, not by a REST call.
        if (data?.sessionId) {
          this.sessionId = data.sessionId;
        }
        if (data?.conversationId) {
          this.conversationId = data.conversationId;
          setConversationId(data.conversationId);
        }
        if (data?.voiceId) {
          this.voiceId = data.voiceId;
        }
        
        // On iOS, notify voiceModeService to set up session tracking and heartbeats.
        // This is iOS-specific because the session was created via WebSocket (not REST).
        // On web/Android, voiceModeService.startSession() already handled this via REST.
        if (Platform.OS === 'ios') {
          try {
            const voiceModeService = require('@/lib/voiceMode').default;
            voiceModeService.onWebSocketSessionReady(data);
          } catch (e) {
            console.error('[VoiceSocket] Failed to notify voiceModeService:', e);
          }
        }
        
        if (this.onStateChange) this.onStateChange('listening');
      },
      state: (data) => {
        devLog('[VoiceSocket] State change:', data?.state);
        const { setState } = useVoiceModeStore.getState().actions;
        this._lastStateChangeTime = Date.now();
        switch (data?.state) {
          case 'listening':
            setState(VOICE_MODE_STATES.LISTENING);
            break;
          case 'processing':
            setState(VOICE_MODE_STATES.PROCESSING);
            break;
          case 'speaking':
            setState(VOICE_MODE_STATES.SPEAKING);
            this._resetSpeechDetectionState();
            break;
        }
        if (this.onStateChange) this.onStateChange(data?.state);
      },
      transcript: (data) => {
        const { text, isFinal } = data || {};
        devLog('[VoiceSocket] Transcript:', text?.substring?.(0, 50), '...', isFinal ? '(final)' : '(partial)');
        const { setUserTranscript, setPartialTranscript } = useVoiceModeStore.getState().actions;
        if (isFinal) {
          setUserTranscript(text);
          setPartialTranscript('');
          this.pendingTranscriptText = text;
          if (this.pendingTranscriptTimeout) {
            clearTimeout(this.pendingTranscriptTimeout);
            this.pendingTranscriptTimeout = null;
          }
          this.pendingTranscriptTimeout = setTimeout(() => {
            if (!this.pendingTranscriptText) return;
            this._addMessageToChat('user', this.pendingTranscriptText);
            this.pendingTranscriptText = null;
            this.pendingTranscriptTimeout = null;
          }, 8000);
        } else {
          setPartialTranscript(text);
        }
        if (this.onTranscript) this.onTranscript(text, isFinal);
      },
      transcript_rejected: (data) => {
        devLog('[VoiceSocket] Transcript rejected:', data?.reason);
        this.pendingTranscriptText = null;
        if (this.pendingTranscriptTimeout) {
          clearTimeout(this.pendingTranscriptTimeout);
          this.pendingTranscriptTimeout = null;
        }
        const { setState, setPartialTranscript } = useVoiceModeStore.getState().actions;
        setState(VOICE_MODE_STATES.LISTENING);
        setPartialTranscript('');
        if (this.onStateChange) this.onStateChange(data?.state || 'listening');
      },
      ai_response: (data) => {
        const { text, intent, isMediaGeneration } = data || {};
        devLog('[VoiceSocket] AI response:', text?.substring?.(0, 50), '...', intent, isMediaGeneration ? '(media)' : '');
        const { setAiTranscript } = useVoiceModeStore.getState().actions;
        setAiTranscript(text);
        if (this.pendingTranscriptText) {
          if (this.pendingTranscriptTimeout) {
            clearTimeout(this.pendingTranscriptTimeout);
            this.pendingTranscriptTimeout = null;
          }
          if (!isMediaGeneration) this._addMessageToChat('user', this.pendingTranscriptText);
          this.pendingTranscriptText = null;
        }
        const citations = this._pendingSearchCitations;
        this._addMessageToChat('assistant', text, citations);
        this._pendingSearchCitations = null;
        if (this.onAiResponse) this.onAiResponse(text, intent);
      },
      search_results: (data) => {
        const results = Array.isArray(data?.results) ? data.results : [];
        const citations = Array.isArray(data?.citations) ? data.citations : [];
        this._pendingSearchCitations = citations.length > 0 ? citations : null;
        useVoiceModeStore.getState().actions.setSearchResults?.({
          query: data?.query || '',
          results,
          citations,
        });
      },
      audio: async (data) => {
        const { buffer, sampleRate, isLast } = data || {};
        devLog('[VoiceSocket] Audio chunk received, isLast:', isLast);
        try {
          await this._queueAudioChunk(buffer, sampleRate || 24000);
        } catch (err) {
          console.error('[VoiceSocket] Error processing audio:', err);
        }
      },
      interrupt_ack: () => {
        devLog('[VoiceSocket] Interrupt acknowledged');
        this._stopAudioPlayback();
      },
      voice_changed: (data) => {
        if (data?.voiceId) {
          this.voiceId = data.voiceId;
          const { setSelectedVoice } = useVoiceModeStore.getState().actions;
          setSelectedVoice(data.voiceId);
        }
      },
      error: (data) => {
        console.error('[VoiceSocket] Error:', data?.message);
        if (this.onError) this.onError(data?.message || 'Unknown error');
      },
      idle_warning: (data) => {
        devLog('[VoiceSocket] Idle warning:', data?.secondsRemaining, 'seconds remaining');
      },
      idle_timeout: (data) => {
        console.log('[VoiceSocket] Idle timeout — session ended:', data?.message);
        this.isConnected = false;
        this._stopAudioPlayback();
        if (this.onError) this.onError(data?.message || 'Session ended due to inactivity');
      },
      stopped: (data) => {
        devLog('[VoiceSocket] Session stopped:', data?.message);
        this.isConnected = false;
        this._stopAudioPlayback();
      },
      rate_limited: (data) => {
        devLog('[VoiceSocket] Rate limited on:', data?.event, '-', data?.message);
      },
      pong: (data) => {
        // Response to our keep-alive ping — no action needed
      },
      vision_result: (data) => {
        devLog('[VoiceSocket] Vision result:', data?.description?.substring?.(0, 80));
        if (this.onVisionContext) this.onVisionContext(data);
      },
      session_resumable: (data) => {
        devLog('[VoiceSocket] Resumable session found:', data);
        const { sessionId } = data || {};
        this.resumableSessionId = sessionId;
        if (sessionId) this.resumeSession(sessionId);
        if (this.onSessionResumable) this.onSessionResumable(data);
      },
      resume_success: (data) => {
        devLog('[VoiceSocket] Session resumed after', data?.disconnectDuration, 'ms');
        const { setState } = useVoiceModeStore.getState().actions;
        this.isResuming = false;
        this.sessionId = data?.sessionId;
        this.conversationId = data?.conversationId;
        switch (data?.state) {
          case 'listening': setState(VOICE_MODE_STATES.LISTENING); break;
          case 'processing': setState(VOICE_MODE_STATES.PROCESSING); break;
          case 'speaking': setState(VOICE_MODE_STATES.SPEAKING); break;
          default: setState(VOICE_MODE_STATES.LISTENING);
        }
        if (this.onStateChange) this.onStateChange(data?.state || 'listening');
        if (this.onResumeSuccess) this.onResumeSuccess(data);
      },
      resume_failed: (data) => {
        devLog('[VoiceSocket] Resume failed:', data?.reason);
        this.isResuming = false;
        this.resumableSessionId = null;
        const failedSessionId = this.sessionId;
        this.sessionId = null;
        if (this.onResumeFailed) this.onResumeFailed({ ...data, sessionId: failedSessionId });
      },
      vision_started: (data) => {
        const { startVision } = useVoiceModeStore.getState().actions;
        startVision(data?.source || 'camera', data?.config);
        if (this.onVisionStarted) this.onVisionStarted(data);
      },
      vision_context: (data) => {
        const { setVisualContext } = useVoiceModeStore.getState().actions;
        if (data?.visualContext) setVisualContext(data.visualContext);
        try {
          require('@/lib/voiceVision').default.onFrameProcessed();
        } catch (e) {}
        if (this.onVisionContext) this.onVisionContext(data);
      },
      vision_stopped: (data) => {
        const { stopVision } = useVoiceModeStore.getState().actions;
        stopVision();
        if (this.onVisionStopped) this.onVisionStopped(data);
      },
      vision_error: (data) => {
        if (this.onVisionError) this.onVisionError(data?.message);
      },
    };

    this.socket.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        const { event: eventName, data } = msg;
        if (eventName && handlers[eventName]) {
          handlers[eventName](data || {});
        }
      } catch (err) {
        console.warn('[VoiceSocket] Raw WebSocket parse error:', err?.message);
      }
    };

    this.socket.onclose = (event) => {
      this.isConnected = false;
      this._stopRawWsPing();
      this._stopAudioPlayback();
      const reason = event?.reason || 'unknown';
      const code = event?.code;
      devLog('[VoiceSocket] Raw WebSocket closed:', code, reason);
      if (code !== 1000) {
        // Abnormal close — keep session for potential resume
        this.resumableSessionId = this.sessionId;
      } else {
        this.sessionId = null;
        this.resumableSessionId = null;
      }
    };

    // Start keep-alive ping every 25 seconds
    this._startRawWsPing();
  }

  /**
   * Start raw WebSocket keep-alive ping interval
   */
  _startRawWsPing() {
    this._stopRawWsPing();
    this._rawWsPingInterval = setInterval(() => {
      this._emitRaw('ping', {});
    }, 25000);
  }

  /**
   * Stop raw WebSocket keep-alive ping interval
   */
  _stopRawWsPing() {
    if (this._rawWsPingInterval) {
      clearInterval(this._rawWsPingInterval);
      this._rawWsPingInterval = null;
    }
  }

  /**
   * Send event via raw WebSocket (iOS)
   */
  _emitRaw(event, data = {}) {
    if (!this._useRawWebSocket || !this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    try {
      this.socket.send(JSON.stringify({ event, data }));
    } catch (err) {
      console.error('[VoiceSocket] Raw WebSocket send error:', err?.message);
    }
  }

  /**
   * Emit event (Socket.IO or raw WebSocket)
   */
  _emit(event, dataOrPayload, extra) {
    if (this._useRealtimeWebRTC) {
      if (event === 'stop') {
        // Only ask OpenAI to cancel a response if one is actually in flight.
        // Sending an unconditional `response.cancel` when the assistant is
        // already idle triggers the benign `Cancellation failed: no active
        // response found` error which then surfaces in the UI as a
        // "Connection Error" modal on iOS right after a normal turn.
        if (this._responseInFlight) {
          const sent = this._sendRealtimeEvent({
            type: 'response.cancel',
            ...(this._currentResponseId ? { response_id: this._currentResponseId } : {}),
          });
          if (sent) {
            this._lastCancelSentAt = Date.now();
            this._responseInFlight = false;
          }
        }
      }
      return;
    }
    if (this._useRawWebSocket) {
      const data = event === 'audio_chunk'
        ? { buffer: dataOrPayload, format: extra?.format || 'audio/mp4' }
        : (typeof dataOrPayload === 'object' && dataOrPayload !== null ? dataOrPayload : {});
      this._emitRaw(event, data);
    } else if (this.socket?.emit) {
      if (event === 'audio_chunk') {
        this.socket.emit(event, dataOrPayload, extra || {});
      } else {
        this.socket.emit(event, dataOrPayload || {});
      }
    }
  }

  /**
   * Reset speech detection state.
   * Called when state transitions to SPEAKING to prevent TTS echo from being
   * treated as user speech. Also resets the MediaRecorder to discard any
   * accumulated audio that was just TTS echo.
   */
  _resetSpeechDetectionState() {
    this.speechDetected = false;
    this.speechStartTime = 0;
    this.hasAudioChunks = false;
    this.audioChunks = [];
    this.lastSoundTime = null;
    this.debugStatus.speechDetected = false;
    devLog('[VoiceSocket] Speech detection state reset (entering speaking/processing)');
    
    // Recreate MediaRecorder to discard any buffered audio from TTS echo
    if (Platform.OS === 'web' && this.audioStream && this.audioStream.active) {
      this._createMediaRecorder();
    }
  }

  /**
   * Add message to chat store
   */
  _addMessageToChat(role, content, citations = null) {
    if (!content) {
      console.warn('[VoiceSocket] Skipping chat insert - missing content', { role });
      return;
    }

    // Use store conversationId as fallback when instance variable is null
    // (race condition: messages arrive before connect() sets this.conversationId)
    const storeConversationId = useVoiceModeStore.getState().conversationId;
    const conversationIdToUse = this.conversationId || storeConversationId;

    if (!conversationIdToUse) {
      console.warn('[VoiceSocket] Skipping chat insert - no conversationId available', {
        role,
        contentLength: typeof content === 'string' ? content.length : 0,
        instanceConversationId: this.conversationId,
        storeConversationId,
      });
      return;
    }
    
    const chatStore = useChatStore.getState();
    const chat = chatStore.chats.find(c => c.id === conversationIdToUse);
    
    // Dedup guard against the same event being processed twice in quick
    // succession. Deliberately time-bounded: matching on role+content alone
    // dropped legitimate repeats (a user saying "yes" twice in one call
    // never showed the second "yes" locally, even though the backend saved
    // it — the chat then changed after a refresh).
    const DUPLICATE_WINDOW_MS = 5000;
    const recentCutoff = Date.now() - DUPLICATE_WINDOW_MS;
    const existingMessage = chat?.messages?.find(m =>
      m.role === role &&
      m.content === content &&
      m.isVoiceMessage &&
      m.timestamp &&
      new Date(m.timestamp).getTime() > recentCutoff
    );
    
    if (!existingMessage) {
      const tempId = `voice-${role}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
      const message = {
        id: tempId,
        role,
        content,
        timestamp: new Date().toISOString(),
        isVoiceMessage: true,
        parameters: { source: 'voice' },
      };
      if (Array.isArray(citations) && citations.length > 0) {
        message.citations = citations;
      }
      chatStore.addMessage(conversationIdToUse, message);
    }
  }

  /**
   * Start the voice session
   */
  start() {
    if (!this._isConnected()) {
      console.error('[VoiceSocket] Not connected');
      return;
    }

    console.log('[VoiceSocket] Starting session');
    if (this._useRealtimeWebRTC) {
      const { setState } = useVoiceModeStore.getState().actions;
      setState(VOICE_MODE_STATES.LISTENING);
      this.onStateChange?.('listening');
      return;
    }
    let timezone;
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (_) { /* optional */ }

    this._emit('start', {
      voiceId: this.voiceId,
      conversationId: this.conversationId,
      ...(timezone ? { timezone } : {}),
    });
  }

  /**
   * Start capturing audio from microphone
   */
  async startAudioCapture() {
    if (this._useRealtimeWebRTC) {
      return true;
    }

    // Use native audio manager on non-web platforms
    if (Platform.OS !== 'web') {
      return this._startNativeAudioCapture();
    }

    // Web implementation
    try {
      devLog('[VoiceSocket] Requesting microphone access...');
      
      // Request microphone access
      this.audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        }
      });
      
      // Log audio track info for debugging
      const audioTracks = this.audioStream.getAudioTracks();
      devLog('[VoiceSocket] Got audio stream with', audioTracks.length, 'track(s)');
      audioTracks.forEach((track, i) => {
        devLog(`[VoiceSocket] Track ${i}: ${track.label}, enabled: ${track.enabled}, readyState: ${track.readyState}`);
      });

      // Pick the best supported audio container/codec
      // iOS Safari only supports mp4/aac, so we need to include those
      const preferredTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg',
        'audio/mp4',  // iOS Safari
        'audio/aac',  // iOS Safari fallback
      ];
      this.supportedMimeType = preferredTypes.find(type => MediaRecorder.isTypeSupported(type));
      
      // Log which formats are supported for debugging
      const supportedFormats = preferredTypes.filter(t => MediaRecorder.isTypeSupported(t));
      devLog('[VoiceSocket] Supported audio formats:', supportedFormats);
      devLog('[VoiceSocket] Using audio format:', this.supportedMimeType || 'browser default');
      devLog('[VoiceSocket] iOS detected:', this.isIOS, '- VAD threshold:', this.vadThreshold);
      
      // Update debug status
      this.debugStatus.audioFormat = this.supportedMimeType || 'browser default';
      this.debugStatus.supportedFormats = supportedFormats;
      
      this.recorderOptions = this.supportedMimeType
        ? { mimeType: this.supportedMimeType, audioBitsPerSecond: 128000 }
        : { audioBitsPerSecond: 128000 };

      // CRITICAL: Initialize AudioContext before creating MediaRecorder (iOS needs this for VAD)
      await this._initAudioContext();

      // Create fresh MediaRecorder
      this._createMediaRecorder();

      // Set up VAD for interruption detection
      this._setupVAD();

      return true;
    } catch (err) {
      console.error('[VoiceSocket] Failed to start audio capture:', err);
      throw err;
    }
  }

  /**
   * Start native audio capture using expo-av
   */
  async _startNativeAudioCapture() {
    try {
      console.log('[VoiceSocket] Starting native audio capture...');
      const audioManager = getNativeAudioManager();

      // IMPORTANT: Set error callback BEFORE initialization so init errors are captured
      audioManager.onError = (error) => {
        console.error('[VoiceSocket] Native audio error:', error);
        this.onError?.(error);
      };

      // Initialize native audio (sets audio mode, runs smoke test).
      // Skip permission check — the VoiceModeOverlay already called
      // ensureNativeMicrophonePermission() and the button's handlePress
      // also requested permission. Triple-requesting on iOS causes the
      // system dialog to misfire or return stale cached results.
      await audioManager.initializeAudio({ skipPermissionCheck: true });

      // Set playback complete callback
      audioManager.onPlaybackComplete = () => {
        console.log('[VoiceSocket] Native playback complete');
        if (!this._isConnected() || !useVoiceModeStore.getState().isActive) {
          return;
        }
        // Return to listening state
        const { setState } = useVoiceModeStore.getState().actions;
        setState(VOICE_MODE_STATES.LISTENING);
        if (this.onStateChange) {
          this.onStateChange('listening');
        }
      };

      // Start recording with utterance callback.
      // Pass skipAudioModeSetup: true because initializeAudio() JUST configured
      // the audio session. Calling setAudioModeAsync again would thrash the iOS
      // audio session (deactivate/reactivate), which can disconnect the mic route
      // and cause the recording to capture silence instead of actual audio.
      await audioManager.startRecording(
        (base64Audio, mimeType) => {
          this._handleNativeUtteranceEnd(base64Audio, mimeType);
        },
        { skipAudioModeSetup: true }
      );

      console.log('[VoiceSocket] Native audio capture started successfully');
      return true;
    } catch (err) {
      console.error('[VoiceSocket] Failed to start native audio capture:', err);
      const message = String(err?.message || err);
      // Keep a consistent error shape so UI can show the right recovery path.
      if (message.includes('MICROPHONE_PERMANENTLY_DENIED')) {
        throw new Error('MICROPHONE_PERMANENTLY_DENIED');
      }
      if (
        message.includes('MICROPHONE_DENIED') ||
        message.toLowerCase().includes('permission denied') ||
        message.toLowerCase().includes('microphone')
      ) {
        throw new Error('MICROPHONE_DENIED');
      }
      throw new Error(`NATIVE_AUDIO_START_FAILED: ${message}`);
    }
  }

  /**
   * Handle utterance end from native audio manager.
   *
   * On native platforms, the first recording starts during startAudioCapture()
   * which completes BEFORE start() emits the 'start' event.  The server then
   * responds with 'ready', which transitions the state to LISTENING.  If the
   * first silence-detection fires before that 'ready' arrives, the state is
   * still CONNECTING — and the original strict check would silently discard
   * the user's speech.
   *
   * CONNECTING is safe to send audio because no TTS playback has started yet,
   * so there is zero echo risk.  We only block during SPEAKING / PROCESSING
   * (when TTS may be playing through the speaker and the mic would pick it up).
   */
  _handleNativeUtteranceEnd(base64Audio, mimeType) {
    if (!this._isConnected()) {
      console.log('[VoiceSocket] Socket not connected, discarding native audio');
      return;
    }

    // Allow audio during LISTENING (normal) and CONNECTING (first utterance
    // before server 'ready' arrives).
    // Also allow during PROCESSING if state just changed (< 500ms) — on iOS
    // the native silence-detection callback can fire after the state already
    // transitioned away from LISTENING but the audio is still valid user speech.
    // Block during SPEAKING (TTS echo) and longer PROCESSING windows.
    const currentState = useVoiceModeStore.getState().state;
    const timeSinceStateChange = Date.now() - (this._lastStateChangeTime || 0);
    const isAllowedState =
      currentState === VOICE_MODE_STATES.LISTENING ||
      currentState === VOICE_MODE_STATES.CONNECTING ||
      (currentState === VOICE_MODE_STATES.PROCESSING && timeSinceStateChange < 500);

    if (!isAllowedState) {
      console.log('[VoiceSocket] Discarding native audio (echo prevention)', {
        state: currentState,
        timeSinceStateChange,
        hasConversationId: !!this.conversationId,
        estimatedBase64Bytes: base64Audio ? Math.floor((base64Audio.length * 3) / 4) : 0,
        mimeType,
      });
      return;
    }

    console.log('[VoiceSocket] Sending native audio utterance, format:', mimeType, 'state:', currentState);
    this._emit('audio_chunk', base64Audio, { format: mimeType });
    this._emit('end_utterance', { format: mimeType });
  }

  /**
   * Create a fresh MediaRecorder instance
   * Must be called for each new utterance to get proper WebM headers
   */
  _createMediaRecorder() {
    // Disconnect old handler FIRST to prevent async ondataavailable from firing
    if (this.mediaRecorder) {
      this.mediaRecorder.ondataavailable = null;
      if (this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
    }

    // Clear chunks AFTER disconnecting old handler to prevent race conditions
    this.audioChunks = [];
    this.hasAudioChunks = false;
    
    // Reset speech detection state for new utterance
    this.speechDetected = false;
    this.speechStartTime = 0;

    // Create new MediaRecorder
    this.mediaRecorder = new MediaRecorder(this.audioStream, this.recorderOptions);
    this.recorderMimeType = this.mediaRecorder.mimeType || this.supportedMimeType || 'audio/webm';
    devLog('[VoiceSocket] Created fresh MediaRecorder, mimeType:', this.recorderMimeType, 'state:', this.mediaRecorder.state);

    // Track chunk count for debugging
    let chunkCount = 0;

    // Capture audio data when available
    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunkCount++;
        
        // Only log occasionally to reduce noise
        if (chunkCount === 1 || chunkCount % 20 === 0) {
          devLog(`[VoiceSocket] Audio chunk #${chunkCount}: ${event.data.size} bytes`);
        }
        
        // Log header of first chunk for debugging
        if (chunkCount === 1) {
          event.data.slice(0, 16).arrayBuffer().then(buf => {
            const bytes = new Uint8Array(buf);
            const headerHex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
            const isValidWebM = headerHex.startsWith('1A45DFA3');
            const isValidMP4 = headerHex.includes('66747970');
            devLog('[VoiceSocket] First chunk header:', headerHex, '- Valid WebM:', isValidWebM, '- Valid MP4:', isValidMP4);
          });
        }
        
        this.audioChunks.push(event.data);
        
        // Only update debug counter when speech is detected (to avoid confusion)
        if (this.speechDetected) {
          this.debugStatus.chunksReceived = chunkCount;
        }
      }
    };
    
    // Add error handler for MediaRecorder (important for iOS debugging)
    this.mediaRecorder.onerror = (event) => {
      const errorMsg = `MediaRecorder error: ${event.error?.name} - ${event.error?.message}`;
      console.error('[VoiceSocket]', errorMsg);
      this._addDebugError(errorMsg);
    };
    
    // Log when recording starts (useful for iOS debugging)
    // NOTE: onstart fires asynchronously, so mediaRecorder may be null if user stops quickly
    this.mediaRecorder.onstart = () => {
      if (!this.mediaRecorder) {
        devLog('[VoiceSocket] MediaRecorder started but already cleaned up (user stopped quickly)');
        return;
      }
      devLog('[VoiceSocket] MediaRecorder started, state:', this.mediaRecorder.state);
      this.debugStatus.mediaRecorderState = this.mediaRecorder.state;
    };

    // Start recording
    // iOS: Use timeslice because iOS Safari may not fire ondataavailable properly without it
    // Desktop: No timeslice for complete valid files with proper headers
    if (this.isIOS) {
      this.mediaRecorder.start(100); // 100ms chunks on iOS
      devLog('[VoiceSocket] Audio capture started (iOS chunked mode), MediaRecorder state:', this.mediaRecorder.state);
    } else {
      this.mediaRecorder.start(); // Continuous on desktop
      devLog('[VoiceSocket] Audio capture started (desktop continuous mode), MediaRecorder state:', this.mediaRecorder.state);
    }
  }

  /**
   * Check if audioContext is valid and not closed
   */
  _isAudioContextValid() {
    return this.audioContext && this.audioContext.state !== 'closed';
  }

  /**
   * Set up Voice Activity Detection for interruption
   * 
   * PERFORMANCE NOTE: This VAD implementation runs on the main thread via setInterval.
   * For improved performance, especially on mobile devices or during heavy UI updates,
   * consider migrating to AudioWorklet which processes audio in a separate thread:
   * 
   * Benefits of AudioWorklet:
   * - Runs in a dedicated audio thread, not affected by main thread blocking
   * - More precise timing for voice activity detection
   * - Lower latency for barge-in detection
   * - Better battery efficiency on mobile
   * 
   * Implementation would involve:
   * 1. Create a VadProcessor class extending AudioWorkletProcessor
   * 2. Load the worklet via audioContext.audioWorklet.addModule()
   * 3. Create AudioWorkletNode and connect to audio source
   * 4. Use MessagePort to communicate VAD events to main thread
   * 
   * See: https://developer.mozilla.org/en-US/docs/Web/API/AudioWorklet
   */
  _setupVAD() {
    if (!this._isAudioContextValid() || !this.audioStream) {
      devWarn('[VoiceSocket] Cannot setup VAD - audioContext valid:', this._isAudioContextValid(), 'audioStream:', !!this.audioStream);
      return;
    }

    try {
      // CRITICAL: Ensure AudioContext is resumed before creating nodes (iOS requirement)
      if (this.audioContext.state === 'suspended') {
        devLog('[VoiceSocket] Resuming AudioContext for VAD setup...');
        this.audioContext.resume().then(() => {
          devLog('[VoiceSocket] AudioContext resumed for VAD, state:', this.audioContext.state);
        }).catch(err => {
          console.error('[VoiceSocket] Failed to resume AudioContext for VAD:', err);
        });
      }
      
      // Store the source node so we can disconnect it later to release the microphone
      this.vadSourceNode = this.audioContext.createMediaStreamSource(this.audioStream);
      this.vadAnalyser = this.audioContext.createAnalyser();
      this.vadAnalyser.fftSize = 256;
      this.vadSourceNode.connect(this.vadAnalyser);
      this.lastSoundTime = Date.now();
      this.lastEndUtteranceTime = 0;
      
      devLog('[VoiceSocket] VAD analyser created, frequencyBinCount:', this.vadAnalyser.frequencyBinCount);

      // Check for user voice during AI playback
      // TODO: Consider migrating to AudioWorklet for better performance
      let vadTickCount = 0;
      this.vadCheckInterval = setInterval(() => {
        vadTickCount++;
        
        // Check if AudioContext got suspended (can happen on iOS when app backgrounded)
        if (this.audioContext?.state === 'suspended') {
          this.audioContext.resume().catch(() => {});
          this.debugStatus.audioContextState = 'suspended (resuming)';
        } else {
          this.debugStatus.audioContextState = this.audioContext?.state;
        }
        
        const dataArray = new Uint8Array(this.vadAnalyser.frequencyBinCount);
        this.vadAnalyser.getByteFrequencyData(dataArray);
        const avgVolume = dataArray.reduce((a, b) => a + b) / dataArray.length;
        
        // Update debug status with current volume (every 5th tick to reduce overhead)
        if (vadTickCount % 5 === 0) {
          this.debugStatus.lastVolume = Math.round(avgVolume);
          this.debugStatus.speechDetected = this.speechDetected;
        }

        const now = Date.now();
        const isSpeaking = avgVolume > this.vadThreshold;
        const currentState = useVoiceModeStore.getState().state;
        
        // CRITICAL: During SPEAKING or PROCESSING states, ignore all audio input.
        // The microphone picks up TTS audio (echo), and without this guard,
        // Whisper would re-transcribe the AI's speech as user input, causing
        // duplicate messages. We only track speech during LISTENING state.
        const isListening = currentState === VOICE_MODE_STATES.LISTENING;
        
        // Grace period: if we detected speech recently, treat brief dips as still speaking
        // This handles natural pauses between words (e.g., "Hello... my name is...")
        const gracePeriodMs = this.isIOS ? 400 : 200; // Longer grace for iOS
        const wasRecentlySpeaking = this.lastSoundTime && (now - this.lastSoundTime) < gracePeriodMs;
        const effectivelySpeaking = isSpeaking || (this.speechDetected && wasRecentlySpeaking);

        if (effectivelySpeaking && isListening) {
          if (isSpeaking) {
            this.lastSoundTime = now; // Only update when actually above threshold
          }
          // Mark as having audio if recorder is running (chunks come when stop() is called)
          if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
            this.hasAudioChunks = true;
          }
          
          // Track speech detection for Whisper hallucination prevention
          if (!this.speechDetected) {
            this.speechDetected = true;
            this.speechStartTime = now;
            this.debugStatus.speechDetected = true;
            devLog('[VoiceSocket] Speech detected, starting utterance, volume:', avgVolume);
          }
        }

        if (this.isPlaying && effectivelySpeaking) {
          devLog('[VoiceSocket] User interruption detected');
          this._handleInterruption();
          return;
        }

        // Only check for end of utterance if speech was actually detected AND we're listening
        if (isListening && this.hasAudioChunks && this.speechDetected) {
          const silenceDuration = now - this.lastSoundTime;
          const timeSinceLastEnd = now - this.lastEndUtteranceTime;
          const speechDuration = now - this.speechStartTime;

          // Require minimum speech duration before allowing end of utterance
          if (silenceDuration >= this.silenceThresholdMs && 
              timeSinceLastEnd >= this.vadDebounceMs &&
              speechDuration >= this.minSpeechDurationMs) {
            devLog('[VoiceSocket] Silence detected after', speechDuration, 'ms of speech, ending utterance');
            this.endUtterance();
            this.lastEndUtteranceTime = now;
            this.hasAudioChunks = false;
          }
        }
      }, 100);

      devLog('[VoiceSocket] VAD setup complete');
    } catch (err) {
      console.error('[VoiceSocket] Failed to setup VAD:', err);
    }
  }

  /**
   * Handle user interruption (barge-in)
   */
  _handleInterruption() {
    if (!this.isPlaying) return;

    devLog('[VoiceSocket] Interrupting playback');
    this._stopAudioPlayback();
    
    // Create fresh MediaRecorder for the new utterance (needs WebM header)
    // This also clears audioChunks and hasAudioChunks
    if (this.audioStream && this.audioStream.active) {
      this._createMediaRecorder();
    }
    
    if (this._isConnected()) {
      this._emit('interrupt', {});
    }
  }

  /**
   * Queue audio chunk for playback
   */
  async _queueAudioChunk(base64Buffer, sampleRate = 24000) {
    // Use native audio manager on non-web platforms
    if (Platform.OS !== 'web') {
      try {
        await getNativeAudioManager().queueAudio(base64Buffer);
      } catch (err) {
        console.error('[VoiceSocket] Failed to queue native audio:', err);
      }
      return;
    }

    // Web implementation
    if (!this.audioContext) {
      await this._initAudioContext();
    }

    // Check if context is valid (may have been closed during disconnect)
    if (!this._isAudioContextValid()) {
      devWarn('[VoiceSocket] AudioContext is closed, cannot queue audio');
      return;
    }

    // Resume audio context if suspended
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    // Decode base64 to ArrayBuffer
    const binary = atob(base64Buffer);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    // Decode audio data
    try {
      const audioBuffer = await this.audioContext.decodeAudioData(bytes.buffer.slice(0));
      this.audioQueue.push(audioBuffer);
      
      if (!this.isPlaying) {
        this._playNextChunk();
      }
    } catch (err) {
      console.error('[VoiceSocket] Failed to decode audio:', err);
    }
  }

  /**
   * Play next audio chunk from queue
   */
  _playNextChunk() {
    if (this.audioQueue.length === 0) {
      this.isPlaying = false;
      return;
    }

    // Check if context is valid before playing
    if (!this._isAudioContextValid()) {
      devWarn('[VoiceSocket] AudioContext is closed, cannot play audio');
      this.audioQueue = [];
      this.isPlaying = false;
      return;
    }

    this.isPlaying = true;
    const audioBuffer = this.audioQueue.shift();

    this.currentSource = this.audioContext.createBufferSource();
    this.currentSource.buffer = audioBuffer;
    this.currentSource.connect(this.audioContext.destination);
    
    this.currentSource.onended = () => {
      this._playNextChunk();
    };

    this.currentSource.start(0);
  }

  /**
   * Stop audio playback
   */
  _stopAudioPlayback() {
    if (this._useRealtimeWebRTC) {
      // Realtime audio is owned by the peer connection, not by our legacy
      // nativeAudioManager/Web Audio playback queues.
      return;
    }

    // Use native audio manager on non-web platforms
    if (Platform.OS !== 'web') {
      Promise.resolve(getNativeAudioManager().stopPlayback()).catch((err) => {
        console.error('[VoiceSocket] Failed to stop native playback:', err?.message || err);
      });
      return;
    }

    // Web implementation
    if (this.currentSource) {
      try {
        this.currentSource.stop();
      } catch (e) {
        // Ignore
      }
      this.currentSource = null;
    }
    this.audioQueue = [];
    this.isPlaying = false;
  }

  /**
   * Stop audio capture
   */
  stopAudioCapture() {
    console.log('[VoiceSocket] Stopping audio capture...');

    if (this._useRealtimeWebRTC) {
      if (this.vadCheckInterval) {
        clearInterval(this.vadCheckInterval);
        this.vadCheckInterval = null;
      }
      if (this.audioStream) {
        this.audioStream.getTracks?.().forEach((track) => {
          try { track.stop?.(); } catch (e) {}
        });
        this.audioStream = null;
      }
      return;
    }

    // Use native audio manager on non-web platforms
    if (Platform.OS !== 'web') {
      Promise.resolve(getNativeAudioManager().stopRecording()).catch((err) => {
        console.error('[VoiceSocket] Failed to stop native recording:', err?.message || err);
      });
      console.log('[VoiceSocket] Native audio capture stopped');
      return;
    }
    
    // Web implementation. In Realtime WebRTC mode there is no MediaRecorder,
    // just the microphone track attached to the peer connection.
    // Clear VAD interval first
    if (this.vadCheckInterval) {
      clearInterval(this.vadCheckInterval);
      this.vadCheckInterval = null;
    }
    this.lastSoundTime = null;
    this.lastEndUtteranceTime = null;
    this.hasAudioChunks = false;
    this.audioChunks = [];
    this.speechDetected = false;
    this.speechStartTime = 0;

    // Stop MediaRecorder
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop();
      } catch (e) {
        devWarn('[VoiceSocket] Error stopping MediaRecorder:', e);
      }
    }
    this.mediaRecorder = null;

    // CRITICAL: Disconnect VAD source node BEFORE stopping tracks
    // This releases the AudioContext's reference to the MediaStream
    if (this.vadSourceNode) {
      try {
        this.vadSourceNode.disconnect();
      } catch (e) {
        // Ignore - may already be disconnected
      }
      this.vadSourceNode = null;
    }
    
    // Clear VAD analyser
    if (this.vadAnalyser) {
      try {
        this.vadAnalyser.disconnect();
      } catch (e) {
        // Ignore - may already be disconnected
      }
      this.vadAnalyser = null;
    }

    // Stop all tracks on the audio stream to release the microphone
    if (this.audioStream) {
      const tracks = this.audioStream.getTracks();
      devLog('[VoiceSocket] Stopping', tracks.length, 'audio tracks');
      tracks.forEach(track => {
        devLog('[VoiceSocket] Stopping track:', track.kind, track.label, 'readyState:', track.readyState);
        track.stop();
      });
      this.audioStream = null;
    }

    devLog('[VoiceSocket] Audio capture stopped');
  }

  /**
   * Ensure audio capture is still working after camera operations.
   * On some mobile browsers, starting a camera getUserMedia can disrupt
   * an existing audio stream. This method checks and restarts if needed.
   */
  ensureAudioCapture() {
    // Check if we have an audio stream and if it's still active
    if (this.audioStream) {
      const audioTracks = this.audioStream.getAudioTracks();
      const hasActiveTracks = audioTracks.some(track => track.readyState === 'live');
      
      if (hasActiveTracks) {
        // Audio stream is still active, check if MediaRecorder needs restart
        if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
          devLog('[VoiceSocket] Audio stream active but MediaRecorder inactive, recreating');
          this._createMediaRecorder();
          this._setupVAD();
        }
        return;
      }
      
      devLog('[VoiceSocket] Audio tracks ended, need to restart audio capture');
    }
    
    // Audio stream is not active, need to restart
    // This will be called asynchronously to not block camera operations
    devLog('[VoiceSocket] Restarting audio capture after camera operation');
    this.startAudioCapture().catch(err => {
      console.error('[VoiceSocket] Failed to restart audio capture:', err);
      if (this.onError) {
        this.onError(err?.message || 'Failed to restart audio capture');
      }
    });
  }

  /**
   * Calculate RMS energy of audio blob to detect if it contains actual speech
   * Returns a value between 0-1, where higher values indicate more audio energy
   */
  async _calculateAudioEnergy(audioBlob) {
    let tempContext = null;
    try {
      const arrayBuffer = await audioBlob.arrayBuffer();
      tempContext = new (window.AudioContext || window.webkitAudioContext)();
      
      const audioBuffer = await tempContext.decodeAudioData(arrayBuffer);
      const channelData = audioBuffer.getChannelData(0);
      
      // Calculate RMS (Root Mean Square) energy
      let sumSquares = 0;
      let peakCount = 0;
      const peakThreshold = 0.05;
      
      for (let i = 0; i < channelData.length; i++) {
        const sample = channelData[i];
        sumSquares += sample * sample;
        // Count samples that exceed peak threshold
        if (Math.abs(sample) > peakThreshold) {
          peakCount++;
        }
      }
      
      const rms = Math.sqrt(sumSquares / channelData.length);
      const peakRatio = peakCount / channelData.length;
      
      return { rms, peakRatio, sampleCount: channelData.length };
    } catch (err) {
      devWarn('[VoiceSocket] Audio energy calculation failed:', err.message);
      return null;
    } finally {
      // Always close the AudioContext to prevent memory leaks
      if (tempContext) {
        try {
          tempContext.close();
        } catch (closeErr) {
          // Ignore close errors - context may already be closed
        }
      }
    }
  }

  /**
   * Signal end of user utterance - sends buffered audio as one blob
   * Creates a fresh MediaRecorder for the next utterance (required for WebM headers)
   */
  endUtterance() {
    if (!this._isConnected()) return;
    
    // Don't send audio during SPEAKING or PROCESSING states - prevents TTS echo
    const currentState = useVoiceModeStore.getState().state;
    if (currentState !== VOICE_MODE_STATES.LISTENING) {
      devLog('[VoiceSocket] endUtterance called but state is', currentState, '- discarding audio (echo prevention)');
      this._resetSpeechDetectionState();
      return;
    }
    
    // Don't send audio if no actual speech was detected - prevents Whisper hallucinations
    if (!this.speechDetected) {
      devLog('[VoiceSocket] No speech detected, ignoring silence trigger');
      this.audioChunks = [];
      this.hasAudioChunks = false;
      // Create fresh recorder for next attempt
      if (this.audioStream && this.audioStream.active) {
        this._createMediaRecorder();
      }
      return;
    }
    
    // Check if MediaRecorder is running - chunks will arrive when we stop it
    if (!this.mediaRecorder || this.mediaRecorder.state !== 'recording') {
      devLog('[VoiceSocket] endUtterance called but MediaRecorder not recording, state:', this.mediaRecorder?.state);
      return;
    }
    
    // Capture current state before async operations
    const mimeType = this.recorderMimeType || 'audio/webm';
    // Minimum audio bytes to avoid sending too-short clips (checked after stop)
    const minAudioBytes = this.isIOS ? 5000 : 10000;
    
    devLog('[VoiceSocket] endUtterance: have', this.audioChunks.length, 'chunks, mimeType:', mimeType);
    
    // Log first chunk header for debugging
    if (this.audioChunks.length > 0 && this.audioChunks[0].size > 0) {
      const firstChunk = this.audioChunks[0];
      firstChunk.slice(0, 16).arrayBuffer().then(buf => {
        const bytes = new Uint8Array(buf);
        const headerHex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
        const isValidWebM = headerHex.startsWith('1A45DFA3');
        const isValidMP4 = headerHex.includes('66747970'); // 'ftyp' in hex
        devLog('[VoiceSocket] First chunk header:', headerHex, '- Valid WebM:', isValidWebM, '- Valid MP4:', isValidMP4);
        if (!isValidWebM && !isValidMP4) {
          console.error('[VoiceSocket] WARNING: First chunk does not have valid audio file header!');
        }
      });
    }

    // Reset speech detection state (but DON'T clear audioChunks yet - wait for onstop)
    this.hasAudioChunks = false;
    this.speechDetected = false;
    this.speechStartTime = 0;

    // Process and send the audio
    const processAndSend = (finalChunks) => {
      if (finalChunks.length === 0) {
        devLog('[VoiceSocket] No audio chunks after stop, skipping');
        if (this.audioStream && this.audioStream.active) {
          this._createMediaRecorder();
        }
        return;
      }
      
      const audioBlob = new Blob(finalChunks, { type: mimeType });
      const blobSize = audioBlob.size;
      
      devLog('[VoiceSocket] Created blob:', blobSize, 'bytes from', finalChunks.length, 'chunks');
      
      // Check minimum size
      if (blobSize < minAudioBytes) {
        devLog('[VoiceSocket] Audio too short (', blobSize, 'bytes, need', minAudioBytes, '), ignoring');
        if (this.audioStream && this.audioStream.active) {
          this._createMediaRecorder();
        }
        return;
      }

      // Analyze audio energy before sending to prevent Whisper hallucinations on silence/noise
      this._calculateAudioEnergy(audioBlob).then((energyData) => {
        // If analysis succeeded and energy is too low, skip sending
        if (energyData !== null) {
          const { rms, peakRatio, sampleCount } = energyData;
          devLog('[VoiceSocket] Audio energy analysis:', {
            rms: rms.toFixed(4),
            peakRatio: (peakRatio * 100).toFixed(2) + '%',
            samples: sampleCount,
            threshold: this.minAudioEnergyRms
          });
          
          // Reject audio that's mostly silence/noise
          if (rms < this.minAudioEnergyRms && peakRatio < 0.01) {
            devLog('[VoiceSocket] Audio energy too low, skipping');
            if (this.audioStream && this.audioStream.active) {
              this._createMediaRecorder();
            }
            return;
          }
        }
        
        // Energy check passed, proceed to send
        devLog('[VoiceSocket] Sending audio blob:', blobSize, 'bytes,', mimeType);
        
        const reader = new FileReader();
        reader.onload = () => {
          if (!this._isConnected()) {
            devLog('[VoiceSocket] Socket disconnected, discarding audio');
            return;
          }
          const base64 = reader.result.split(',')[1];
          this._emit('audio_chunk', base64, { format: mimeType });
          this._emit('end_utterance', { format: mimeType });
          devLog('[VoiceSocket] Audio sent, format:', mimeType, 'chunks:', finalChunks.length);
          
          // Create fresh MediaRecorder for next utterance
          if (this.audioStream && this.audioStream.active) {
            this._createMediaRecorder();
          }
        };
        reader.onerror = (err) => {
          console.error('[VoiceSocket] Failed to read audio blob:', err);
          if (this.audioStream && this.audioStream.active) {
            this._createMediaRecorder();
          }
        };
        reader.readAsDataURL(audioBlob);
      }).catch((err) => {
        console.error('[VoiceSocket] Energy analysis failed:', err);
        // Fallback: send anyway
        const reader = new FileReader();
        reader.onload = () => {
          if (!this._isConnected()) return;
          const base64 = reader.result.split(',')[1];
          this._emit('audio_chunk', base64, { format: mimeType });
          this._emit('end_utterance', { format: mimeType });
          if (this.audioStream && this.audioStream.active) {
            this._createMediaRecorder();
          }
        };
        reader.readAsDataURL(audioBlob);
      });
    };

    // If MediaRecorder is active, stop it and wait for onstop
    // The stop() call may trigger a final ondataavailable with remaining buffered data
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      // Set up one-time onstop handler to process after stop completes
      this.mediaRecorder.onstop = () => {
        // Now it's safe to grab all chunks (including any final one from stop())
        const finalChunks = [...this.audioChunks];
        this.audioChunks = []; // Clear for next utterance
        devLog('[VoiceSocket] MediaRecorder stopped, processing', finalChunks.length, 'chunks');
        this.mediaRecorder.onstop = null; // Clean up
        processAndSend(finalChunks);
      };
      this.mediaRecorder.stop();
    } else {
      // Recorder already inactive, process immediately
      const finalChunks = [...this.audioChunks];
      this.audioChunks = []; // Clear for next utterance
      processAndSend(finalChunks);
    }
  }

  /**
   * Change TTS voice
   */
  changeVoice(voiceId) {
    if (this._useRealtimeWebRTC) {
      devWarn('[VoiceSocket] Realtime voice changes apply on the next session');
      return;
    }
    if (this._isConnected()) {
      this._emit('change_voice', { voiceId });
    }
  }

  // ============ Vision Methods ============

  /**
   * Whether the active transport is OpenAI Realtime WebRTC (vs legacy
   * Socket.IO / raw WebSocket). The vision service uses this to route
   * frames over REST + data-channel context injection, since the socket
   * vision events are no-ops on the realtime path.
   */
  isRealtimeTransport() {
    return this._useRealtimeWebRTC;
  }

  /**
   * Inject a visual-context description into the realtime conversation as
   * a system item so the model can "see" what the camera / shared screen
   * currently shows. Identical consecutive descriptions are deduped.
   * @param {string} description - Visual context text from frame analysis
   * @param {string} source - 'camera' or 'screen'
   * @returns {boolean} true if the event was sent over the data channel
   */
  injectVisualContext(description, source = 'camera') {
    if (!this._useRealtimeWebRTC) return false;
    const text = (description || '').trim();
    if (!text || text === this._lastInjectedVisualContext) return false;

    const surface = source === 'screen' ? 'shared screen' : 'camera';
    const sent = this._sendRealtimeEvent({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'system',
        content: [
          {
            type: 'input_text',
            text: `[Visual context — what the user's ${surface} currently shows]: ${text}`,
          },
        ],
      },
    });
    if (sent) {
      this._lastInjectedVisualContext = text;
      devLog('[VoiceSocket] Injected visual context into realtime session');
    }
    return sent;
  }

  /**
   * Start vision mode (camera or screen share)
   * @param {string} source - 'camera' or 'screen'
   */
  startVision(source = 'camera') {
    if (!this._isConnected()) {
      console.error('[VoiceSocket] Cannot start vision - not connected');
      return false;
    }
    devLog('[VoiceSocket] Starting vision, source:', source);
    this._emit('vision_start', { source });
    return true;
  }

  /**
   * Send a video frame for analysis
   * @param {string} base64JpegData - Base64 encoded JPEG image (max 500KB)
   */
  sendVisionFrame(base64JpegData) {
    if (!this._isConnected()) {
      return false;
    }
    // Raw WebSocket expects { buffer }; Socket.IO expects { frame }
    const payload = this._useRawWebSocket
      ? { buffer: base64JpegData }
      : { frame: base64JpegData };
    this._emit('vision_frame', payload);
    return true;
  }

  /**
   * Stop vision mode
   */
  stopVision() {
    if (!this._isConnected()) {
      return false;
    }
    devLog('[VoiceSocket] Stopping vision');
    this._emit('vision_stop', {});
    return true;
  }

  // ============ VAD Methods ============

  /**
   * Set VAD sensitivity threshold
   */
  setVadThreshold(threshold) {
    this.vadThreshold = threshold;
  }

  /**
   * Configure VAD parameters
   * Note: Both iOS and desktop benefit from lower thresholds for better speech detection
   */
  setVadConfig({ silenceThresholdMs, volumeThreshold, debounceMs } = {}) {
    if (typeof silenceThresholdMs === 'number') {
      // iOS needs longer silence threshold to handle natural speech pauses
      this.silenceThresholdMs = this.isIOS ? Math.max(silenceThresholdMs, 1200) : silenceThresholdMs;
    }
    if (typeof volumeThreshold === 'number') {
      // Fixed threshold for speech detection (ignore backend value, use our tuned value)
      // Must be high enough to not trigger on AI's TTS audio playing through speakers
      this.vadThreshold = 35;
      devLog('[VoiceSocket] VAD threshold set:', this.vadThreshold);
    }
    if (typeof debounceMs === 'number') {
      this.vadDebounceMs = debounceMs;
    }
  }

  /**
   * Mute/unmute microphone
   */
  setMuted(muted) {
    if (this._useRealtimeWebRTC && this.audioStream) {
      this.audioStream.getAudioTracks?.().forEach(track => {
        track.enabled = !muted;
      });
      return;
    }

    // Use native audio manager on non-web platforms
    if (Platform.OS !== 'web') {
      getNativeAudioManager().setMuted(muted);
      return;
    }

    // Web implementation
    if (this.audioStream) {
      this.audioStream.getAudioTracks().forEach(track => {
        track.enabled = !muted;
      });
    }
  }

  /**
   * Get current input volume level (0-1)
   */
  getInputVolume() {
    if (this._useRealtimeWebRTC && Platform.OS === 'macos') {
      return require('./voiceRealtimeWebRTCAdapter.macos').getMacVoiceInputVolume();
    }
    // Native Realtime WebRTC does not expose metering through expo-av.
    // Keep the UI stable; web still gets analyser-based levels below.
    if (this._useRealtimeWebRTC && Platform.OS !== 'web') {
      return 0;
    }

    // On native, delegate to nativeAudioManager which reads expo-av metering
    if (Platform.OS !== 'web') {
      return getNativeAudioManager().getInputVolume();
    }
    
    if (!this.vadAnalyser) return 0;
    
    const dataArray = new Uint8Array(this.vadAnalyser.frequencyBinCount);
    this.vadAnalyser.getByteFrequencyData(dataArray);
    const avgVolume = dataArray.reduce((a, b) => a + b) / dataArray.length;
    return Math.min(avgVolume / 128, 1); // Normalize to 0-1
  }

  /**
   * Get debug status for troubleshooting (useful on mobile where console isn't visible)
   */
  getDebugStatus() {
    return {
      ...this.debugStatus,
      isIOS: this.isIOS,
      vadThreshold: this.vadThreshold,
      isConnected: this.isConnected,
      hasAudioStream: !!this.audioStream,
      audioStreamActive: this.audioStream?.active,
      audioTracksLive: this.audioStream?.getAudioTracks?.()?.filter(t => t.readyState === 'live').length || 0,
      mediaRecorderState: this.mediaRecorder?.state,
      audioContextState: this.audioContext?.state,
      audioChunksCount: this.audioChunks?.length || 0,
      speechDetected: this.speechDetected,
      currentVolume: this.getInputVolume(),
    };
  }

  /**
   * Add error to debug status
   */
  _addDebugError(error) {
    this.debugStatus.errors.push({
      time: new Date().toISOString(),
      message: error,
    });
    // Keep only last 5 errors
    if (this.debugStatus.errors.length > 5) {
      this.debugStatus.errors.shift();
    }
  }

  /**
   * Attempt to resume session on reconnection
   * Called internally when socket reconnects after unexpected disconnect
   */
  _attemptResumeOnReconnect() {
    if (!this.sessionId) {
      devLog('[VoiceSocket] No session ID to resume');
      return;
    }

    // Guard against duplicate resume attempts
    if (this.isResuming) {
      devLog('[VoiceSocket] Already attempting to resume session, skipping duplicate');
      return;
    }

    // Set resuming state in store
    const { setState } = useVoiceModeStore.getState().actions;
    setState(VOICE_MODE_STATES.RESUMING);
    
    this.isResuming = true;
    
    // Emit resume request - the server will respond with resume_success or resume_failed
    this.resumeSession(this.sessionId);
  }

  /**
   * Attempt to resume a session
   * @param {string} sessionId - The session ID to resume
   */
  resumeSession(sessionId) {
    if (!this._isConnected()) {
      console.error('[VoiceSocket] Cannot resume session - not connected');
      return false;
    }

    if (!sessionId) {
      console.error('[VoiceSocket] Cannot resume session - no session ID provided');
      return false;
    }

    // Guard against duplicate resume attempts (unless called from _attemptResumeOnReconnect which already sets isResuming)
    if (this.isResuming) {
      devLog('[VoiceSocket] Resume already in progress, emitting resume_session');
      this._emit('resume_session', { sessionId });
      return true;
    }

    devLog('[VoiceSocket] Attempting to resume session:', sessionId);
    
    const { setState } = useVoiceModeStore.getState().actions;
    setState(VOICE_MODE_STATES.RESUMING);
    this.isResuming = true;

    this._emit('resume_session', { sessionId });
    return true;
  }

  /**
   * Check if there's a resumable session
   * @returns {string|null} The resumable session ID or null
   */
  getResumableSessionId() {
    return this.resumableSessionId;
  }

  /**
   * Check if currently attempting to resume
   * @returns {boolean}
   */
  isResumingSession() {
    return this.isResuming;
  }

  /**
   * Stop the voice session
   */
  stop() {
    devLog('[VoiceSocket] Stopping session');

    // Clear any buffered transcript
    this.pendingTranscriptText = null;
    if (this.pendingTranscriptTimeout) {
      clearTimeout(this.pendingTranscriptTimeout);
      this.pendingTranscriptTimeout = null;
    }
    this._pendingSearchCitations = null;
    this._pendingSearchQuery = null;
    this._webSearchInFlight = false;
    useVoiceModeStore.getState().actions.clearSearchResults?.();
    
    if (this._isConnected()) {
      this._emit('stop', {});
    }

    this.stopAudioCapture();
    this._stopAudioPlayback();
  }

  /**
   * Disconnect from voice socket
   */
  disconnect() {
    console.log('[VoiceSocket] Disconnecting...');

    // Cancel any pending transient-disconnect grace timer so it can't fire
    // after we've torn the peer connection down.
    if (this._disconnectTimeout) {
      clearTimeout(this._disconnectTimeout);
      this._disconnectTimeout = null;
    }

    // Stop audio capture and playback first (this releases the microphone)
    try {
      this.stop();
    } catch (err) {
      console.error('[VoiceSocket] Error in stop():', err);
    }
    
    // CRITICAL: Ensure microphone is released even if stop() failed
    // Double-check audio cleanup in case stop() didn't complete
    this._ensureMicrophoneReleased();

    if (this.dataChannel) {
      try {
        this.dataChannel.close();
      } catch (e) {}
      this.dataChannel = null;
    }

    if (this.peerConnection) {
      try {
        this.peerConnection.getSenders?.().forEach((sender) => {
          try { sender.track?.stop?.(); } catch (e) {}
        });
        this.peerConnection.close();
      } catch (e) {}
      this.peerConnection = null;
    }

    if (this.remoteAudioElement) {
      try {
        this.remoteAudioElement.pause?.();
        this.remoteAudioElement.srcObject = null;
      } catch (e) {}
      this.remoteAudioElement = null;
    }
    this.remoteAudioStream = null;

    if (this._useRealtimeWebRTC && Platform.OS !== 'web') {
      Promise.resolve(restoreNativeRealtimeAudio()).catch((err) => {
        devWarn('[VoiceSocket] Failed to restore native realtime audio route:', err?.message || err);
      });
    }

    if (this.socket) {
      if (this._useRealtimeWebRTC) {
        // Data channel was already closed above.
      } else if (this._useRawWebSocket) {
        this._stopRawWsPing();
        try {
          this.socket.close();
        } catch (e) {}
      } else {
        this.socket.off('connect');
        this.socket.off('disconnect');
        this.socket.off('ready');
        this.socket.off('state');
        this.socket.off('transcript');
        this.socket.off('transcript_rejected');
        this.socket.off('ai_response');
        this.socket.off('audio');
        this.socket.off('interrupt_ack');
        this.socket.off('voice_changed');
        this.socket.off('error');
        this.socket.off('vision_started');
        this.socket.off('vision_context');
        this.socket.off('vision_stopped');
        this.socket.off('vision_error');
        this.socket.off('session_resumable');
        this.socket.off('resume_success');
        this.socket.off('resume_failed');
        this.socket.disconnect();
      }
      this.socket = null;
      this._useRawWebSocket = false;
      this._useRealtimeWebRTC = false;
    }

    if (this.audioContext) {
      try {
        // Only close if not already closed
        if (this.audioContext.state !== 'closed') {
          this.audioContext.close();
        }
      } catch (err) {
        devWarn('[VoiceSocket] Error closing audio context:', err.message);
      }
      this.audioContext = null;
    }

    this.isConnected = false;
    this.sessionId = null;
    this.conversationId = null;
    this.voiceId = null;
    this._useRealtimeWebRTC = false;
    this._useRawWebSocket = false;
    this._assistantTranscriptBuffer = '';
    this._assistantTextBuffer = '';

    // Clear realtime turn tracking so the next session starts clean.
    this._committedResponseIds = new Set();
    this._lastUserTranscriptItemId = null;
    this._currentResponseId = null;
    this._responseInFlight = false;
    this._userSpeechActive = false;
    this._responseAudioStartedAt = 0;
    this._suppressingLikelyEcho = false;
    this._iosTurnDetection = null;
    this._iosVadSuspended = false;
    this._clearIosVadRestoreTimer();
    this._clearIosBargeInTimer();
    this._iosBargeInPending = false;
    this._nativeRemoteTrackRouteConfigured = false;
    this._lastCancelSentAt = 0;
    this._lastInjectedVisualContext = null;
    // Drop any pending-saves bookkeeping for the next session. The actual
    // promises (if any survived the flush in handleEndCall) are detached and
    // will no-op against the next session's sessionId.
    this._pendingTranscriptSaves = new Set();

    // Clear session resumption state
    this.isResuming = false;
    this.resumableSessionId = null;
    this.disconnectReason = null;

    devLog('[VoiceSocket] Disconnected');
  }
  
  /**
   * Ensure microphone is fully released
   * Called as a safety net after stop() to guarantee cleanup
   */
  _ensureMicrophoneReleased() {
    // Stop any remaining VAD interval
    if (this.vadCheckInterval) {
      clearInterval(this.vadCheckInterval);
      this.vadCheckInterval = null;
    }
    
    // Disconnect VAD source node
    if (this.vadSourceNode) {
      try {
        this.vadSourceNode.disconnect();
      } catch (e) { /* ignore */ }
      this.vadSourceNode = null;
    }
    
    // Disconnect VAD analyser
    if (this.vadAnalyser) {
      try {
        this.vadAnalyser.disconnect();
      } catch (e) { /* ignore */ }
      this.vadAnalyser = null;
    }
    
    // Stop MediaRecorder
    if (this.mediaRecorder) {
      try {
        if (this.mediaRecorder.state !== 'inactive') {
          this.mediaRecorder.stop();
        }
      } catch (e) { /* ignore */ }
      this.mediaRecorder = null;
    }
    
    // CRITICAL: Stop all tracks on the audio stream
    if (this.audioStream) {
      try {
        const tracks = this.audioStream.getTracks();
        tracks.forEach(track => {
          if (track.readyState === 'live') {
            track.stop();
          }
        });
      } catch (e) {
        devWarn('[VoiceSocket] Error stopping audio tracks:', e);
      }
      this.audioStream = null;
    }

    // Release a prewarmed mic stream that was never consumed by connect()
    // (e.g. session start failed before the WebRTC connection was made).
    if (this._prewarmedStream) {
      try {
        this._prewarmedStream.getTracks().forEach((track) => track.stop());
      } catch (e) { /* ignore */ }
      this._prewarmedStream = null;
    }
    this._micPrewarmPromise = null;
  }

  /**
   * Check if connected
   */
  isSocketConnected() {
    return this.isConnected && this._isConnected();
  }
}

// Export singleton instance
const voiceSocketManager = new VoiceSocketManager();
export default voiceSocketManager;
